// Auto-généré à partir de periodic_table.sql
import { elementIons } from './ions-data';
import { isotopeUses } from './isotopes-uses';

export type RadiationType = 'alpha' | 'beta-' | 'beta+' | 'gamma' | 'stable' | 'neutron' | 'proton';

export interface Ion {
  symbol: string;           // ex: "Na⁺", "Cl⁻", "Fe²⁺", "O²⁻"
  charge: number;           // ex: +1, -1, +2, -2
  type: 'cation' | 'anion'; // cation = positif, anion = négatif
  name: string;             // nom de l'ion ex: "Sodium", "Chlorure"
  uses: string;             // utilisations de cet ion
  color?: string;           // couleur optionnelle d'affichage
}

export interface Isotope {
  massNumber: number;
  symbol: string;
  halfLife: string; // ex: "12.32 ans", "5730 ans", "stable", "10.3 min"
  halfLifeSeconds?: number; // demi-vie en secondes pour tri et calculs
  decayMode: RadiationType[];
  naturalAbundance?: number; // en pourcentage (0-100)
  isStable: boolean;
  uses?: string; // applications médicales, industrielles, scientifiques
}

export interface Element {
  id: number;
  atomicNumber: number;
  symbol: string;
  name: string;
  category: string;
  electronConfiguration: string;
  atomicWeight: number;
  state: 'solid' | 'liquid' | 'gas';
  density?: number;
  meltingPoint?: number;
  boilingPoint?: number;
  electronegativity?: number;
  discoveredBy?: string;
  yearDiscovered?: number;
  description: string;
  uses: string;
  color: string;
  group?: number;
  period: number;
  gridRow: number;
  gridColumn: number;
  isotopes?: Isotope[];
  ions?: Ion[];
}

export const elements: Element[] = [
  {
    id: 1,
    atomicNumber: 1,
    symbol: 'H',
    name: 'Hydrogène',
    category: 'nonmetal',
    electronConfiguration: '1s¹',
    atomicWeight: 1.00794,
    state: 'gas',
    density: 0.08988,
    meltingPoint: 14.01,
    boilingPoint: 20.28,
    electronegativity: 2.20,
    discoveredBy: 'Henry Cavendish',
    yearDiscovered: 1766,
    description: "L'hydrogène est l'élément chimique le plus léger et le plus abondant de l'univers.",
    uses: "Production d'ammoniac, raffinage du pétrole, carburant pour fusées.",
    color: '#64ffda',
    period: 1,
    gridRow: 1,
    gridColumn: 1,
    isotopes: [
      {
            "massNumber": 1,
            "symbol": "¹H",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 99.98,
            "isStable": true
      },
      {
            "massNumber": 2,
            "symbol": "²H",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.02,
            "isStable": true
      },
      {
            "massNumber": 3,
            "symbol": "³H",
            "halfLife": "12.32 ans",
            "halfLifeSeconds": 388800000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 2,
    atomicNumber: 2,
    symbol: 'He',
    name: 'Hélium',
    category: 'noble_gas',
    electronConfiguration: '1s²',
    atomicWeight: 4.00260,
    state: 'gas',
    density: 0.17850,
    meltingPoint: 0.95,
    boilingPoint: 4.22,
    discoveredBy: 'Pierre Janssen',
    yearDiscovered: 1868,
    description: "L'hélium est un gaz noble inerte et le deuxième élément le plus abondant dans l'univers.",
    uses: 'Ballons, dirigeables, refroidissement des aimants supraconducteurs.',
    color: '#c792ea',
    period: 1,
    gridRow: 1,
    gridColumn: 18,
    isotopes: [
      {
            "massNumber": 3,
            "symbol": "³He",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.0002,
            "isStable": true
      },
      {
            "massNumber": 4,
            "symbol": "⁴He",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 99.9998,
            "isStable": true
      }
]
  },
  {
    id: 3,
    atomicNumber: 3,
    symbol: 'Li',
    name: 'Lithium',
    category: 'alkali_metal',
    electronConfiguration: '1s² 2s¹',
    atomicWeight: 6.94100,
    state: 'solid',
    density: 0.53400,
    meltingPoint: 453.69,
    boilingPoint: 1615.00,
    electronegativity: 0.98,
    discoveredBy: 'Johan August Arfwedson',
    yearDiscovered: 1817,
    description: "Le lithium est le métal solide le plus léger et possède une grande capacité de stockage d'énergie.",
    uses: 'Batteries lithium-ion, médicaments, alliages légers, verre et céramique.',
    color: '#ff5370',
    period: 2,
    gridRow: 2,
    gridColumn: 1,
    isotopes: [
      {
            "massNumber": 6,
            "symbol": "⁶Li",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 7.59,
            "isStable": true
      },
      {
            "massNumber": 7,
            "symbol": "⁷Li",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 92.41,
            "isStable": true
      }
]
  },
  {
    id: 4,
    atomicNumber: 4,
    symbol: 'Be',
    name: 'Béryllium',
    category: 'alkaline_earth_metal',
    electronConfiguration: '1s² 2s²',
    atomicWeight: 9.01218,
    state: 'solid',
    density: 1.85000,
    meltingPoint: 1560.00,
    boilingPoint: 2742.00,
    electronegativity: 1.57,
    discoveredBy: 'Louis Nicolas Vauquelin',
    yearDiscovered: 1797,
    description: 'Le béryllium est un métal alcalino-terreux léger, relativement rare dans la croûte terrestre.',
    uses: "Alliages dans l'aérospatiale, modérateur de neutrons dans les réacteurs nucléaires, rayons X.",
    color: '#f78c6c',
    period: 2,
    gridRow: 2,
    gridColumn: 2,
    isotopes: [
      {
            "massNumber": 7,
            "symbol": "⁷Be",
            "halfLife": "53.29 jours",
            "halfLifeSeconds": 4604000,
            "decayMode": [
                  "gamma"
            ],
            "isStable": false
      },
      {
            "massNumber": 9,
            "symbol": "⁹Be",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      },
      {
            "massNumber": 10,
            "symbol": "¹⁰Be",
            "halfLife": "1.39 millions d'années",
            "halfLifeSeconds": 43900000000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 5,
    atomicNumber: 5,
    symbol: 'B',
    name: 'Bore',
    category: 'metalloid',
    electronConfiguration: '1s² 2s² 2p¹',
    atomicWeight: 10.81100,
    state: 'solid',
    density: 2.34000,
    meltingPoint: 2349.00,
    boilingPoint: 4200.00,
    electronegativity: 2.04,
    discoveredBy: 'Joseph Louis Gay-Lussac',
    yearDiscovered: 1808,
    description: 'Le bore est un métalloïde qui forme des composés similaires au carbone mais avec une variété de structures.',
    uses: 'Verre borosilicaté, détergents, semi-conducteurs, agriculture.',
    color: '#c3e88d',
    period: 2,
    gridRow: 2,
    gridColumn: 13,
    isotopes: [
      {
            "massNumber": 10,
            "symbol": "¹⁰B",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 19.9,
            "isStable": true
      },
      {
            "massNumber": 11,
            "symbol": "¹¹B",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 80.1,
            "isStable": true
      }
]
  },
  {
    id: 6,
    atomicNumber: 6,
    symbol: 'C',
    name: 'Carbone',
    category: 'nonmetal',
    electronConfiguration: '1s² 2s² 2p²',
    atomicWeight: 12.01070,
    state: 'solid',
    density: 2.26700,
    meltingPoint: 3800.00,
    boilingPoint: 4300.00,
    electronegativity: 2.55,
    discoveredBy: 'Anciens',
    description: 'Le carbone est un élément non métallique qui forme la base de la chimie organique.',
    uses: 'Acier, plastiques, nanotubes, diamants, fibres de carbone.',
    color: '#64ffda',
    period: 2,
    gridRow: 2,
    gridColumn: 14,
    isotopes: [
      {
            "massNumber": 12,
            "symbol": "¹²C",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 98.93,
            "isStable": true
      },
      {
            "massNumber": 13,
            "symbol": "¹³C",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 1.07,
            "isStable": true
      },
      {
            "massNumber": 14,
            "symbol": "¹⁴C",
            "halfLife": "5730 ans",
            "halfLifeSeconds": 180800000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 7,
    atomicNumber: 7,
    symbol: 'N',
    name: 'Azote',
    category: 'nonmetal',
    electronConfiguration: '1s² 2s² 2p³',
    atomicWeight: 14.00670,
    state: 'gas',
    density: 1.25060,
    meltingPoint: 63.15,
    boilingPoint: 77.36,
    electronegativity: 3.04,
    discoveredBy: 'Daniel Rutherford',
    yearDiscovered: 1772,
    description: "L'azote constitue environ 78% de l'atmosphère terrestre.",
    uses: "Engrais, explosifs, refroidissement, production d'acide nitrique.",
    color: '#82aaff',
    period: 2,
    gridRow: 2,
    gridColumn: 15,
    isotopes: [
      {
            "massNumber": 14,
            "symbol": "¹⁴N",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 99.636,
            "isStable": true
      },
      {
            "massNumber": 15,
            "symbol": "¹⁵N",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.364,
            "isStable": true
      }
]
  },
  {
    id: 8,
    atomicNumber: 8,
    symbol: 'O',
    name: 'Oxygène',
    category: 'nonmetal',
    electronConfiguration: '1s² 2s² 2p⁴',
    atomicWeight: 15.99940,
    state: 'gas',
    density: 1.42900,
    meltingPoint: 54.36,
    boilingPoint: 90.20,
    electronegativity: 3.44,
    discoveredBy: 'Carl Wilhelm Scheele',
    yearDiscovered: 1771,
    description: "L'oxygène est essentiel à la respiration et à la combustion.",
    uses: 'Médecine, sidérurgie, traitement des eaux, propulseur pour fusées.',
    color: '#89ddff',
    period: 2,
    gridRow: 2,
    gridColumn: 16,
    isotopes: [
      {
            "massNumber": 16,
            "symbol": "¹⁶O",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 99.757,
            "isStable": true
      },
      {
            "massNumber": 17,
            "symbol": "¹⁷O",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.038,
            "isStable": true
      },
      {
            "massNumber": 18,
            "symbol": "¹⁸O",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.205,
            "isStable": true
      }
]
  },
  {
    id: 9,
    atomicNumber: 9,
    symbol: 'F',
    name: 'Fluor',
    category: 'halogen',
    electronConfiguration: '1s² 2s² 2p⁵',
    atomicWeight: 18.99840,
    state: 'gas',
    density: 1.69600,
    meltingPoint: 53.53,
    boilingPoint: 85.03,
    electronegativity: 3.98,
    discoveredBy: 'Henri Moissan',
    yearDiscovered: 1886,
    description: "Le fluor est l'élément le plus électronégatif et réactif du tableau périodique.",
    uses: "Production d'uranium, réfrigérants, dentifrice, polymères comme le Téflon.",
    color: '#ffcb6b',
    period: 2,
    gridRow: 2,
    gridColumn: 17,
    isotopes: [
      {
            "massNumber": 18,
            "symbol": "¹⁸F",
            "halfLife": "109.77 min",
            "halfLifeSeconds": 6586.2,
            "decayMode": [
                  "beta+"
            ],
            "isStable": false
      },
      {
            "massNumber": 19,
            "symbol": "¹⁹F",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      }
]
  },
  {
    id: 10,
    atomicNumber: 10,
    symbol: 'Ne',
    name: 'Néon',
    category: 'noble_gas',
    electronConfiguration: '1s² 2s² 2p⁶',
    atomicWeight: 20.17970,
    state: 'gas',
    density: 0.90020,
    meltingPoint: 24.56,
    boilingPoint: 27.07,
    discoveredBy: 'William Ramsay',
    yearDiscovered: 1898,
    description: "Le néon est un gaz noble incolore, inodore et inerte qui émet une lumière rouge-orangée lorsqu'il est électrisé.",
    uses: 'Enseignes lumineuses, lasers, réfrigération cryogénique, télévisions.',
    color: '#c792ea',
    period: 2,
    gridRow: 2,
    gridColumn: 18,
    isotopes: [
      {
            "massNumber": 20,
            "symbol": "²⁰Ne",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 90.48,
            "isStable": true
      },
      {
            "massNumber": 21,
            "symbol": "²¹Ne",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.27,
            "isStable": true
      },
      {
            "massNumber": 22,
            "symbol": "²²Ne",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 9.25,
            "isStable": true
      }
]
  },
  {
    id: 11,
    atomicNumber: 11,
    symbol: 'Na',
    name: 'Sodium',
    category: 'alkali_metal',
    electronConfiguration: '[Ne] 3s¹',
    atomicWeight: 22.98977,
    state: 'solid',
    density: 0.97100,
    meltingPoint: 370.87,
    boilingPoint: 1156.00,
    electronegativity: 0.93,
    discoveredBy: 'Humphry Davy',
    yearDiscovered: 1807,
    description: 'Le sodium est un métal alcalin réactif et le sixième élément le plus abondant dans la croûte terrestre.',
    uses: 'Production de soude, lampes à vapeur de sodium, refroidisseur dans les réacteurs nucléaires.',
    color: '#ff5370',
    period: 3,
    gridRow: 3,
    gridColumn: 1,
    isotopes: [
      {
            "massNumber": 22,
            "symbol": "²²Na",
            "halfLife": "2.60 ans",
            "halfLifeSeconds": 82000000,
            "decayMode": [
                  "beta+"
            ],
            "isStable": false
      },
      {
            "massNumber": 23,
            "symbol": "²³Na",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      },
      {
            "massNumber": 24,
            "symbol": "²⁴Na",
            "halfLife": "14.96 heures",
            "halfLifeSeconds": 53856,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 12,
    atomicNumber: 12,
    symbol: 'Mg',
    name: 'Magnésium',
    category: 'alkaline_earth_metal',
    electronConfiguration: '[Ne] 3s²',
    atomicWeight: 24.30500,
    state: 'solid',
    density: 1.73800,
    meltingPoint: 923.00,
    boilingPoint: 1363.00,
    electronegativity: 1.31,
    discoveredBy: 'Joseph Black',
    yearDiscovered: 1755,
    description: "Le magnésium est un métal alcalino-terreux léger et silvery-white, le huitième élément le plus abondant dans la croûte terrestre.",
    uses: "Alliages légers, feux d'artifice, supplément alimentaire, réduction chimique.",
    color: '#f78c6c',
    period: 3,
    gridRow: 3,
    gridColumn: 2,
    isotopes: [
      {
            "massNumber": 24,
            "symbol": "²⁴Mg",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 78.99,
            "isStable": true
      },
      {
            "massNumber": 25,
            "symbol": "²⁵Mg",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 10,
            "isStable": true
      },
      {
            "massNumber": 26,
            "symbol": "²⁶Mg",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 11.01,
            "isStable": true
      }
]
  },
  {
    id: 13,
    atomicNumber: 13,
    symbol: 'Al',
    name: 'Aluminium',
    category: 'post_transition_metal',
    electronConfiguration: '[Ne] 3s² 3p¹',
    atomicWeight: 26.98154,
    state: 'solid',
    density: 2.70000,
    meltingPoint: 933.47,
    boilingPoint: 2792.00,
    electronegativity: 1.61,
    discoveredBy: 'Hans Christian Ørsted',
    yearDiscovered: 1825,
    description: "L'aluminium est un métal léger, non magnétique et résistant à la corrosion, le métal le plus abondant dans la croûte terrestre.",
    uses: 'Construction, emballages, transport, électronique.',
    color: '#89ddff',
    period: 3,
    gridRow: 3,
    gridColumn: 13,
    isotopes: [
      {
            "massNumber": 26,
            "symbol": "²⁶Al",
            "halfLife": "717000 ans",
            "halfLifeSeconds": 22600000000000,
            "decayMode": [
                  "beta+"
            ],
            "isStable": false
      },
      {
            "massNumber": 27,
            "symbol": "²⁷Al",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      }
]
  },
  {
    id: 14,
    atomicNumber: 14,
    symbol: 'Si',
    name: 'Silicium',
    category: 'metalloid',
    electronConfiguration: '[Ne] 3s² 3p²',
    atomicWeight: 28.08550,
    state: 'solid',
    density: 2.32900,
    meltingPoint: 1687.00,
    boilingPoint: 3538.00,
    electronegativity: 1.90,
    discoveredBy: 'Jöns Jacob Berzelius',
    yearDiscovered: 1824,
    description: 'Le silicium est un métalloïde semi-conducteur qui forme la base de la microélectronique moderne.',
    uses: 'Semi-conducteurs, cellules solaires, verre, céramique, silicones.',
    color: '#c3e88d',
    period: 3,
    gridRow: 3,
    gridColumn: 14,
    isotopes: [
      {
            "massNumber": 28,
            "symbol": "²⁸Si",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 92.23,
            "isStable": true
      },
      {
            "massNumber": 29,
            "symbol": "²⁹Si",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 4.67,
            "isStable": true
      },
      {
            "massNumber": 30,
            "symbol": "³⁰Si",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 3.1,
            "isStable": true
      }
]
  },
  {
    id: 15,
    atomicNumber: 15,
    symbol: 'P',
    name: 'Phosphore',
    category: 'nonmetal',
    electronConfiguration: '[Ne] 3s² 3p³',
    atomicWeight: 30.97376,
    state: 'solid',
    density: 1.82000,
    meltingPoint: 317.30,
    boilingPoint: 553.65,
    electronegativity: 2.19,
    discoveredBy: 'Hennig Brand',
    yearDiscovered: 1669,
    description: "Le phosphore est un élément non-métallique essentiel pour la vie, qui existe sous plusieurs allotropes.",
    uses: 'Engrais, allumettes, détergents, additifs alimentaires, acides nucléiques (ADN, ARN).',
    color: '#64ffda',
    period: 3,
    gridRow: 3,
    gridColumn: 15,
    isotopes: [
      {
            "massNumber": 31,
            "symbol": "³¹P",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      },
      {
            "massNumber": 32,
            "symbol": "³²P",
            "halfLife": "14.29 jours",
            "halfLifeSeconds": 1235000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      },
      {
            "massNumber": 33,
            "symbol": "³³P",
            "halfLife": "25.34 jours",
            "halfLifeSeconds": 2189000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 16,
    atomicNumber: 16,
    symbol: 'S',
    name: 'Soufre',
    category: 'nonmetal',
    electronConfiguration: '[Ne] 3s² 3p⁴',
    atomicWeight: 32.06500,
    state: 'solid',
    density: 2.06700,
    meltingPoint: 388.36,
    boilingPoint: 717.87,
    electronegativity: 2.58,
    discoveredBy: 'Anciens',
    description: "Le soufre est un élément non-métallique jaune qui a été connu depuis l'antiquité et joue un rôle important dans de nombreux processus biologiques.",
    uses: "Fabrication d'acide sulfurique, poudre à canon, vulcanisation du caoutchouc, fongicide.",
    color: '#ffcb6b',
    period: 3,
    gridRow: 3,
    gridColumn: 16,
    isotopes: [
      {
            "massNumber": 32,
            "symbol": "³²S",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 94.99,
            "isStable": true
      },
      {
            "massNumber": 33,
            "symbol": "³³S",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.75,
            "isStable": true
      },
      {
            "massNumber": 34,
            "symbol": "³⁴S",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 4.25,
            "isStable": true
      },
      {
            "massNumber": 35,
            "symbol": "³⁵S",
            "halfLife": "87.51 jours",
            "halfLifeSeconds": 7561000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 17,
    atomicNumber: 17,
    symbol: 'Cl',
    name: 'Chlore',
    category: 'halogen',
    electronConfiguration: '[Ne] 3s² 3p⁵',
    atomicWeight: 35.45300,
    state: 'gas',
    density: 3.20000,
    meltingPoint: 171.65,
    boilingPoint: 239.11,
    electronegativity: 3.16,
    discoveredBy: 'Carl Wilhelm Scheele',
    yearDiscovered: 1774,
    description: 'Le chlore est un halogène jaune-verdâtre utilisé comme désinfectant.',
    uses: "Désinfection de l'eau, fabrication de produits chimiques, plastiques PVC.",
    color: '#c3e88d',
    period: 3,
    gridRow: 3,
    gridColumn: 17,
    isotopes: [
      {
            "massNumber": 35,
            "symbol": "³⁵Cl",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 75.76,
            "isStable": true
      },
      {
            "massNumber": 36,
            "symbol": "³⁶Cl",
            "halfLife": "301000 ans",
            "halfLifeSeconds": 9490000000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      },
      {
            "massNumber": 37,
            "symbol": "³⁷Cl",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 24.24,
            "isStable": true
      }
]
  },
  {
    id: 18,
    atomicNumber: 18,
    symbol: 'Ar',
    name: 'Argon',
    category: 'noble_gas',
    electronConfiguration: '[Ne] 3s² 3p⁶',
    atomicWeight: 39.94800,
    state: 'gas',
    density: 1.78400,
    meltingPoint: 83.80,
    boilingPoint: 87.30,
    discoveredBy: 'Lord Rayleigh',
    yearDiscovered: 1894,
    description: "L'argon est un gaz noble inerte constituant environ 1% de l'atmosphère terrestre.",
    uses: "Atmosphère protectrice pour soudage, ampoules, isolation de fenêtres, substitut non réactif de l'azote.",
    color: '#c792ea',
    period: 3,
    gridRow: 3,
    gridColumn: 18,
    isotopes: [
      {
            "massNumber": 36,
            "symbol": "³⁶Ar",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.34,
            "isStable": true
      },
      {
            "massNumber": 38,
            "symbol": "³⁸Ar",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.06,
            "isStable": true
      },
      {
            "massNumber": 40,
            "symbol": "⁴⁰Ar",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 99.6,
            "isStable": true
      }
]
  },
  {
    id: 19,
    atomicNumber: 19,
    symbol: 'K',
    name: 'Potassium',
    category: 'alkali_metal',
    electronConfiguration: '[Ar] 4s¹',
    atomicWeight: 39.09830,
    state: 'solid',
    density: 0.86200,
    meltingPoint: 336.53,
    boilingPoint: 1032.00,
    electronegativity: 0.82,
    discoveredBy: 'Humphry Davy',
    yearDiscovered: 1807,
    description: "Le potassium est un métal alcalin hautement réactif et le huitième élément le plus abondant dans la croûte terrestre.",
    uses: "Engrais, savon, traitement de l'eau, applications biomédicales.",
    color: '#ff5370',
    period: 4,
    gridRow: 4,
    gridColumn: 1,
    isotopes: [
      {
            "massNumber": 39,
            "symbol": "³⁹K",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 93.26,
            "isStable": true
      },
      {
            "massNumber": 40,
            "symbol": "⁴⁰K",
            "halfLife": "1.248 milliards d'années",
            "halfLifeSeconds": 39400000000000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      },
      {
            "massNumber": 41,
            "symbol": "⁴¹K",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 6.73,
            "isStable": true
      }
]
  },
  {
    id: 20,
    atomicNumber: 20,
    symbol: 'Ca',
    name: 'Calcium',
    category: 'alkaline_earth_metal',
    electronConfiguration: '[Ar] 4s²',
    atomicWeight: 40.07800,
    state: 'solid',
    density: 1.54000,
    meltingPoint: 1115.00,
    boilingPoint: 1757.00,
    electronegativity: 1.00,
    discoveredBy: 'Humphry Davy',
    yearDiscovered: 1808,
    description: 'Le calcium est un métal alcalino-terreux gris argenté, le cinquième élément le plus abondant dans la croûte terrestre et essentiel pour la vie.',
    uses: 'Construction (ciment, plâtre), suppléments nutritionnels, agent réducteur.',
    color: '#f78c6c',
    period: 4,
    gridRow: 4,
    gridColumn: 2,
    isotopes: [
      {
            "massNumber": 40,
            "symbol": "⁴⁰Ca",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 96.94,
            "isStable": true
      },
      {
            "massNumber": 42,
            "symbol": "⁴²Ca",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.65,
            "isStable": true
      },
      {
            "massNumber": 44,
            "symbol": "⁴⁴Ca",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 2.09,
            "isStable": true
      }
]
  },
  {
    id: 21,
    atomicNumber: 21,
    symbol: 'Sc',
    name: 'Scandium',
    category: 'transition_metal',
    electronConfiguration: '[Ar] 3d¹ 4s²',
    atomicWeight: 44.95591,
    state: 'solid',
    density: 2.98500,
    meltingPoint: 1814.00,
    boilingPoint: 3109.00,
    electronegativity: 1.36,
    discoveredBy: 'Lars Fredrik Nilson',
    yearDiscovered: 1879,
    description: 'Le scandium est un métal de transition léger et argenté.',
    uses: 'Alliages d\'aluminium pour l\'aérospatiale, lampes à vapeur métallique.',
    color: '#f07178',
    period: 4,
    gridRow: 4,
    gridColumn: 3,
    isotopes: [
      {
            "massNumber": 45,
            "symbol": "⁴⁵Sc",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      },
      {
            "massNumber": 46,
            "symbol": "⁴⁶Sc",
            "halfLife": "83.79 jours",
            "halfLifeSeconds": 7239000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 22,
    atomicNumber: 22,
    symbol: 'Ti',
    name: 'Titane',
    category: 'transition_metal',
    electronConfiguration: '[Ar] 3d² 4s²',
    atomicWeight: 47.86700,
    state: 'solid',
    density: 4.50600,
    meltingPoint: 1941.00,
    boilingPoint: 3560.00,
    electronegativity: 1.54,
    discoveredBy: 'William Gregor',
    yearDiscovered: 1791,
    description: 'Le titane est un métal de transition résistant, léger et résistant à la corrosion.',
    uses: 'Aérospatiale, implants médicaux, pigments, alliages.',
    color: '#f07178',
    period: 4,
    gridRow: 4,
    gridColumn: 4,
    isotopes: [
      {
            "massNumber": 46,
            "symbol": "⁴⁶Ti",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 8.25,
            "isStable": true
      },
      {
            "massNumber": 47,
            "symbol": "⁴⁷Ti",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 7.44,
            "isStable": true
      },
      {
            "massNumber": 48,
            "symbol": "⁴⁸Ti",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 73.72,
            "isStable": true
      },
      {
            "massNumber": 49,
            "symbol": "⁴⁹Ti",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 5.41,
            "isStable": true
      },
      {
            "massNumber": 50,
            "symbol": "⁵⁰Ti",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 5.18,
            "isStable": true
      }
]
  },
  {
    id: 23,
    atomicNumber: 23,
    symbol: 'V',
    name: 'Vanadium',
    category: 'transition_metal',
    electronConfiguration: '[Ar] 3d³ 4s²',
    atomicWeight: 50.94150,
    state: 'solid',
    density: 6.11000,
    meltingPoint: 2183.00,
    boilingPoint: 3680.00,
    electronegativity: 1.63,
    discoveredBy: 'Andrés Manuel del Río',
    yearDiscovered: 1801,
    description: 'Le vanadium est utilisé principalement pour améliorer les propriétés de l\'acier.',
    uses: 'Alliages d\'acier, catalyseurs, batteries redox.',
    color: '#f07178',
    period: 4,
    gridRow: 4,
    gridColumn: 5,
    isotopes: [
      {
            "massNumber": 50,
            "symbol": "⁵⁰V",
            "halfLife": "1.4e17 ans",
            "halfLifeSeconds": 4.42e+24,
            "decayMode": [
                  "beta+"
            ],
            "naturalAbundance": 0.25,
            "isStable": false
      },
      {
            "massNumber": 51,
            "symbol": "⁵¹V",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 99.75,
            "isStable": true
      }
]
  },
  {
    id: 24,
    atomicNumber: 24,
    symbol: 'Cr',
    name: 'Chrome',
    category: 'transition_metal',
    electronConfiguration: '[Ar] 3d⁵ 4s¹',
    atomicWeight: 51.99610,
    state: 'solid',
    density: 7.19000,
    meltingPoint: 2180.00,
    boilingPoint: 2944.00,
    electronegativity: 1.66,
    discoveredBy: 'Louis Nicolas Vauquelin',
    yearDiscovered: 1797,
    description: 'Le chrome est un métal dur, brillant et résistant à la corrosion.',
    uses: 'Acier inoxydable, placage chromé, pigments, tannage du cuir.',
    color: '#f07178',
    period: 4,
    gridRow: 4,
    gridColumn: 6,
    isotopes: [
      {
            "massNumber": 50,
            "symbol": "⁵⁰Cr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 4.35,
            "isStable": true
      },
      {
            "massNumber": 52,
            "symbol": "⁵²Cr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 83.79,
            "isStable": true
      },
      {
            "massNumber": 53,
            "symbol": "⁵³Cr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 9.5,
            "isStable": true
      },
      {
            "massNumber": 54,
            "symbol": "⁵⁴Cr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 2.36,
            "isStable": true
      }
]
  },
  {
    id: 25,
    atomicNumber: 25,
    symbol: 'Mn',
    name: 'Manganèse',
    category: 'transition_metal',
    electronConfiguration: '[Ar] 3d⁵ 4s²',
    atomicWeight: 54.93804,
    state: 'solid',
    density: 7.21000,
    meltingPoint: 1519.00,
    boilingPoint: 2334.00,
    electronegativity: 1.55,
    discoveredBy: 'Johan Gottlieb Gahn',
    yearDiscovered: 1774,
    description: 'Le manganèse est essentiel pour la production d\'acier et a un rôle important dans les systèmes biologiques.',
    uses: 'Production d\'acier, piles sèches, alliages d\'aluminium.',
    color: '#f07178',
    period: 4,
    gridRow: 4,
    gridColumn: 7,
    isotopes: [
      {
            "massNumber": 53,
            "symbol": "⁵³Mn",
            "halfLife": "3.7 millions d'années",
            "halfLifeSeconds": 117000000000000,
            "decayMode": [
                  "gamma"
            ],
            "isStable": false
      },
      {
            "massNumber": 54,
            "symbol": "⁵⁴Mn",
            "halfLife": "312.3 jours",
            "halfLifeSeconds": 26980000,
            "decayMode": [
                  "gamma"
            ],
            "isStable": false
      },
      {
            "massNumber": 55,
            "symbol": "⁵⁵Mn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      }
]
  },
  {
    id: 26,
    atomicNumber: 26,
    symbol: 'Fe',
    name: 'Fer',
    category: 'transition_metal',
    electronConfiguration: '[Ar] 3d⁶ 4s²',
    atomicWeight: 55.84500,
    state: 'solid',
    density: 7.87400,
    meltingPoint: 1811.00,
    boilingPoint: 3134.00,
    electronegativity: 1.83,
    discoveredBy: 'Anciens',
    description: 'Le fer est un métal de transition abondant et essentiel qui a été crucial pour le développement de la civilisation humaine.',
    uses: 'Acier, construction, machines, transport, catalyseurs.',
    color: '#f07178',
    period: 4,
    gridRow: 4,
    gridColumn: 8,
    isotopes: [
      {
            "massNumber": 54,
            "symbol": "⁵⁴Fe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 5.85,
            "isStable": true
      },
      {
            "massNumber": 56,
            "symbol": "⁵⁶Fe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 91.75,
            "isStable": true
      },
      {
            "massNumber": 57,
            "symbol": "⁵⁷Fe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 2.12,
            "isStable": true
      },
      {
            "massNumber": 58,
            "symbol": "⁵⁸Fe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.28,
            "isStable": true
      }
]
  },
  {
    id: 27,
    atomicNumber: 27,
    symbol: 'Co',
    name: 'Cobalt',
    category: 'transition_metal',
    electronConfiguration: '[Ar] 3d⁷ 4s²',
    atomicWeight: 58.93319,
    state: 'solid',
    density: 8.90000,
    meltingPoint: 1768.00,
    boilingPoint: 3200.00,
    electronegativity: 1.88,
    discoveredBy: 'Georg Brandt',
    yearDiscovered: 1739,
    description: 'Le cobalt est un métal de transition dur et brillant utilisé dans les alliages et les pigments.',
    uses: 'Batteries lithium-ion, alliages haute température, pigments bleus.',
    color: '#f07178',
    period: 4,
    gridRow: 4,
    gridColumn: 9,
    isotopes: [
      {
            "massNumber": 57,
            "symbol": "⁵⁷Co",
            "halfLife": "271.79 jours",
            "halfLifeSeconds": 23480000,
            "decayMode": [
                  "gamma"
            ],
            "isStable": false
      },
      {
            "massNumber": 59,
            "symbol": "⁵⁹Co",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      },
      {
            "massNumber": 60,
            "symbol": "⁶⁰Co",
            "halfLife": "5.27 ans",
            "halfLifeSeconds": 166300000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 28,
    atomicNumber: 28,
    symbol: 'Ni',
    name: 'Nickel',
    category: 'transition_metal',
    electronConfiguration: '[Ar] 3d⁸ 4s²',
    atomicWeight: 58.69340,
    state: 'solid',
    density: 8.90800,
    meltingPoint: 1728.00,
    boilingPoint: 3186.00,
    electronegativity: 1.91,
    discoveredBy: 'Axel Fredrik Cronstedt',
    yearDiscovered: 1751,
    description: 'Le nickel est un métal résistant à la corrosion utilisé dans les alliages.',
    uses: 'Acier inoxydable, pièces de monnaie, batteries, placage.',
    color: '#f07178',
    period: 4,
    gridRow: 4,
    gridColumn: 10,
    isotopes: [
      {
            "massNumber": 58,
            "symbol": "⁵⁸Ni",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 68.08,
            "isStable": true
      },
      {
            "massNumber": 60,
            "symbol": "⁶⁰Ni",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 26.22,
            "isStable": true
      },
      {
            "massNumber": 61,
            "symbol": "⁶¹Ni",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 1.14,
            "isStable": true
      },
      {
            "massNumber": 62,
            "symbol": "⁶²Ni",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 3.63,
            "isStable": true
      },
      {
            "massNumber": 64,
            "symbol": "⁶⁴Ni",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.93,
            "isStable": true
      }
]
  },
  {
    id: 29,
    atomicNumber: 29,
    symbol: 'Cu',
    name: 'Cuivre',
    category: 'transition_metal',
    electronConfiguration: '[Ar] 3d¹⁰ 4s¹',
    atomicWeight: 63.54600,
    state: 'solid',
    density: 8.96000,
    meltingPoint: 1357.77,
    boilingPoint: 2835.00,
    electronegativity: 1.90,
    discoveredBy: 'Anciens',
    description: "Le cuivre est l'un des premiers métaux exploités par l'homme.",
    uses: 'Fils électriques, tuyauterie, pièces de monnaie, alliages.',
    color: '#f07178',
    period: 4,
    gridRow: 4,
    gridColumn: 11,
    isotopes: [
      {
            "massNumber": 63,
            "symbol": "⁶³Cu",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 69.17,
            "isStable": true
      },
      {
            "massNumber": 64,
            "symbol": "⁶⁴Cu",
            "halfLife": "12.7 heures",
            "halfLifeSeconds": 45720,
            "decayMode": [
                  "beta-",
                  "beta+"
            ],
            "isStable": false
      },
      {
            "massNumber": 65,
            "symbol": "⁶⁵Cu",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 30.83,
            "isStable": true
      }
]
  },
  {
    id: 30,
    atomicNumber: 30,
    symbol: 'Zn',
    name: 'Zinc',
    category: 'transition_metal',
    electronConfiguration: '[Ar] 3d¹⁰ 4s²',
    atomicWeight: 65.38000,
    state: 'solid',
    density: 7.13400,
    meltingPoint: 692.68,
    boilingPoint: 1180.00,
    electronegativity: 1.65,
    discoveredBy: 'Anciens',
    description: 'Le zinc est un métal bleuâtre utilisé pour la galvanisation.',
    uses: 'Galvanisation, alliages (laiton), piles, supplément nutritionnel.',
    color: '#f07178',
    period: 4,
    gridRow: 4,
    gridColumn: 12,
    isotopes: [
      {
            "massNumber": 64,
            "symbol": "⁶⁴Zn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 48.63,
            "isStable": true
      },
      {
            "massNumber": 66,
            "symbol": "⁶⁶Zn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 27.9,
            "isStable": true
      },
      {
            "massNumber": 67,
            "symbol": "⁶⁷Zn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 4.1,
            "isStable": true
      },
      {
            "massNumber": 68,
            "symbol": "⁶⁸Zn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 18.75,
            "isStable": true
      }
]
  },
  {
    id: 31,
    atomicNumber: 31,
    symbol: 'Ga',
    name: 'Gallium',
    category: 'post_transition_metal',
    electronConfiguration: '[Ar] 3d¹⁰ 4s² 4p¹',
    atomicWeight: 69.72300,
    state: 'solid',
    density: 5.90700,
    meltingPoint: 302.91,
    boilingPoint: 2477.00,
    electronegativity: 1.81,
    discoveredBy: 'Paul-Émile Lecoq de Boisbaudran',
    yearDiscovered: 1875,
    description: 'Le gallium fond dans la main et est utilisé en électronique.',
    uses: 'Semi-conducteurs, LED, cellules solaires, thermomètres.',
    color: '#89ddff',
    period: 4,
    gridRow: 4,
    gridColumn: 13,
    isotopes: [
      {
            "massNumber": 69,
            "symbol": "⁶⁹Ga",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 60.11,
            "isStable": true
      },
      {
            "massNumber": 71,
            "symbol": "⁷¹Ga",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 39.89,
            "isStable": true
      }
]
  },
  {
    id: 32,
    atomicNumber: 32,
    symbol: 'Ge',
    name: 'Germanium',
    category: 'metalloid',
    electronConfiguration: '[Ar] 3d¹⁰ 4s² 4p²',
    atomicWeight: 72.64000,
    state: 'solid',
    density: 5.32300,
    meltingPoint: 1211.40,
    boilingPoint: 3106.00,
    electronegativity: 2.01,
    discoveredBy: 'Clemens Winkler',
    yearDiscovered: 1886,
    description: 'Le germanium est un métalloïde utilisé en électronique.',
    uses: 'Transistors, détecteurs infrarouges, fibre optique.',
    color: '#c3e88d',
    period: 4,
    gridRow: 4,
    gridColumn: 14,
    isotopes: [
      {
            "massNumber": 70,
            "symbol": "⁷⁰Ge",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 20.38,
            "isStable": true
      },
      {
            "massNumber": 72,
            "symbol": "⁷²Ge",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 27.31,
            "isStable": true
      },
      {
            "massNumber": 73,
            "symbol": "⁷³Ge",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 7.76,
            "isStable": true
      },
      {
            "massNumber": 74,
            "symbol": "⁷⁴Ge",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 36.72,
            "isStable": true
      }
]
  },
  {
    id: 33,
    atomicNumber: 33,
    symbol: 'As',
    name: 'Arsenic',
    category: 'metalloid',
    electronConfiguration: '[Ar] 3d¹⁰ 4s² 4p³',
    atomicWeight: 74.92160,
    state: 'solid',
    density: 5.77600,
    meltingPoint: 1090.00,
    electronegativity: 2.18,
    discoveredBy: 'Albertus Magnus',
    yearDiscovered: 1250,
    description: 'L\'arsenic est toxique mais utilisé dans certains alliages.',
    uses: 'Alliages de plomb, pesticides, semi-conducteurs.',
    color: '#c3e88d',
    period: 4,
    gridRow: 4,
    gridColumn: 15,
    isotopes: [
      {
            "massNumber": 75,
            "symbol": "⁷⁵As",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      }
]
  },
  {
    id: 34,
    atomicNumber: 34,
    symbol: 'Se',
    name: 'Sélénium',
    category: 'nonmetal',
    electronConfiguration: '[Ar] 3d¹⁰ 4s² 4p⁴',
    atomicWeight: 78.96000,
    state: 'solid',
    density: 4.80900,
    meltingPoint: 494.00,
    boilingPoint: 958.00,
    electronegativity: 2.55,
    discoveredBy: 'Jöns Jacob Berzelius',
    yearDiscovered: 1817,
    description: 'Le sélénium est un élément essentiel à faible dose.',
    uses: 'Photocopieurs, cellules solaires, verre coloré, supplément nutritionnel.',
    color: '#64ffda',
    period: 4,
    gridRow: 4,
    gridColumn: 16,
    isotopes: [
      {
            "massNumber": 74,
            "symbol": "⁷⁴Se",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.89,
            "isStable": true
      },
      {
            "massNumber": 76,
            "symbol": "⁷⁶Se",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 9.37,
            "isStable": true
      },
      {
            "massNumber": 77,
            "symbol": "⁷⁷Se",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 7.63,
            "isStable": true
      },
      {
            "massNumber": 78,
            "symbol": "⁷⁸Se",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 23.77,
            "isStable": true
      },
      {
            "massNumber": 80,
            "symbol": "⁸⁰Se",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 49.61,
            "isStable": true
      }
]
  },
  {
    id: 35,
    atomicNumber: 35,
    symbol: 'Br',
    name: 'Brome',
    category: 'halogen',
    electronConfiguration: '[Ar] 3d¹⁰ 4s² 4p⁵',
    atomicWeight: 79.90400,
    state: 'liquid',
    density: 3.12300,
    meltingPoint: 265.95,
    boilingPoint: 332.00,
    electronegativity: 2.96,
    discoveredBy: 'Antoine Jérôme Balard',
    yearDiscovered: 1826,
    description: 'Le brome est le seul élément non-métallique liquide à température ambiante.',
    uses: 'Retardateurs de flamme, désinfection des piscines, photographie.',
    color: '#ffcb6b',
    period: 4,
    gridRow: 4,
    gridColumn: 17,
    isotopes: [
      {
            "massNumber": 79,
            "symbol": "⁷⁹Br",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 50.69,
            "isStable": true
      },
      {
            "massNumber": 81,
            "symbol": "⁸¹Br",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 49.31,
            "isStable": true
      }
]
  },
  {
    id: 36,
    atomicNumber: 36,
    symbol: 'Kr',
    name: 'Krypton',
    category: 'noble_gas',
    electronConfiguration: '[Ar] 3d¹⁰ 4s² 4p⁶',
    atomicWeight: 83.79800,
    state: 'gas',
    density: 3.74900,
    meltingPoint: 115.79,
    boilingPoint: 119.93,
    discoveredBy: 'William Ramsay',
    yearDiscovered: 1898,
    description: 'Le krypton est un gaz noble utilisé dans les lampes.',
    uses: 'Éclairage, lasers, isolation des fenêtres.',
    color: '#c792ea',
    period: 4,
    gridRow: 4,
    gridColumn: 18,
    isotopes: [
      {
            "massNumber": 78,
            "symbol": "⁷⁸Kr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.35,
            "isStable": true
      },
      {
            "massNumber": 80,
            "symbol": "⁸⁰Kr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 2.28,
            "isStable": true
      },
      {
            "massNumber": 82,
            "symbol": "⁸²Kr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 11.58,
            "isStable": true
      },
      {
            "massNumber": 83,
            "symbol": "⁸³Kr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 11.49,
            "isStable": true
      },
      {
            "massNumber": 84,
            "symbol": "⁸⁴Kr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 57,
            "isStable": true
      },
      {
            "massNumber": 86,
            "symbol": "⁸⁶Kr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 17.3,
            "isStable": true
      }
]
  },
  {
    id: 37,
    atomicNumber: 37,
    symbol: 'Rb',
    name: 'Rubidium',
    category: 'alkali_metal',
    electronConfiguration: '[Kr] 5s¹',
    atomicWeight: 85.46780,
    state: 'solid',
    density: 1.53200,
    meltingPoint: 312.46,
    boilingPoint: 961.00,
    electronegativity: 0.82,
    discoveredBy: 'Robert Bunsen',
    yearDiscovered: 1861,
    description: 'Le rubidium est un métal alcalin très réactif.',
    uses: 'Horloges atomiques, catalyseurs, recherche biomédicale.',
    color: '#ff5370',
    period: 5,
    gridRow: 5,
    gridColumn: 1,
    isotopes: [
      {
            "massNumber": 85,
            "symbol": "⁸⁵Rb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 72.17,
            "isStable": true
      },
      {
            "massNumber": 87,
            "symbol": "⁸⁷Rb",
            "halfLife": "48.8 milliards d'années",
            "halfLifeSeconds": 1540000000000000000,
            "decayMode": [
                  "beta-"
            ],
            "naturalAbundance": 27.83,
            "isStable": false
      }
]
  },
  {
    id: 38,
    atomicNumber: 38,
    symbol: 'Sr',
    name: 'Strontium',
    category: 'alkaline_earth_metal',
    electronConfiguration: '[Kr] 5s²',
    atomicWeight: 87.62000,
    state: 'solid',
    density: 2.64000,
    meltingPoint: 1050.00,
    boilingPoint: 1655.00,
    electronegativity: 0.95,
    discoveredBy: 'William Cruickshank',
    yearDiscovered: 1790,
    description: 'Le strontium produit une flamme rouge brillante.',
    uses: 'Feux d\'artifice, tubes cathodiques, aimants en ferrite.',
    color: '#f78c6c',
    period: 5,
    gridRow: 5,
    gridColumn: 2,
    isotopes: [
      {
            "massNumber": 84,
            "symbol": "⁸⁴Sr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.56,
            "isStable": true
      },
      {
            "massNumber": 86,
            "symbol": "⁸⁶Sr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 9.86,
            "isStable": true
      },
      {
            "massNumber": 87,
            "symbol": "⁸⁷Sr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 7,
            "isStable": true
      },
      {
            "massNumber": 88,
            "symbol": "⁸⁸Sr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 82.58,
            "isStable": true
      },
      {
            "massNumber": 90,
            "symbol": "⁹⁰Sr",
            "halfLife": "28.79 ans",
            "halfLifeSeconds": 908000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 39,
    atomicNumber: 39,
    symbol: 'Y',
    name: 'Yttrium',
    category: 'transition_metal',
    electronConfiguration: '[Kr] 4d¹ 5s²',
    atomicWeight: 88.90585,
    state: 'solid',
    density: 4.47200,
    meltingPoint: 1799.00,
    boilingPoint: 3609.00,
    electronegativity: 1.22,
    discoveredBy: 'Johan Gadolin',
    yearDiscovered: 1794,
    description: 'L\'yttrium est utilisé dans les supraconducteurs et les LED.',
    uses: 'Supraconducteurs, LED, lasers, alliages.',
    color: '#f07178',
    period: 5,
    gridRow: 5,
    gridColumn: 3,
    isotopes: [
      {
            "massNumber": 89,
            "symbol": "⁸⁹Y",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      },
      {
            "massNumber": 90,
            "symbol": "⁹⁰Y",
            "halfLife": "64 heures",
            "halfLifeSeconds": 230400,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 40,
    atomicNumber: 40,
    symbol: 'Zr',
    name: 'Zirconium',
    category: 'transition_metal',
    electronConfiguration: '[Kr] 4d² 5s²',
    atomicWeight: 91.22400,
    state: 'solid',
    density: 6.50600,
    meltingPoint: 2128.00,
    boilingPoint: 4682.00,
    electronegativity: 1.33,
    discoveredBy: 'Martin Heinrich Klaproth',
    yearDiscovered: 1789,
    description: 'Le zirconium est résistant à la corrosion et utilisé dans les réacteurs nucléaires.',
    uses: 'Réacteurs nucléaires, céramiques, joaillerie, implants dentaires.',
    color: '#f07178',
    period: 5,
    gridRow: 5,
    gridColumn: 4,
    isotopes: [
      {
            "massNumber": 90,
            "symbol": "⁹⁰Zr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 51.45,
            "isStable": true
      },
      {
            "massNumber": 91,
            "symbol": "⁹¹Zr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 11.22,
            "isStable": true
      },
      {
            "massNumber": 92,
            "symbol": "⁹²Zr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 17.15,
            "isStable": true
      },
      {
            "massNumber": 94,
            "symbol": "⁹⁴Zr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 17.38,
            "isStable": true
      }
]
  },
  {
    id: 41,
    atomicNumber: 41,
    symbol: 'Nb',
    name: 'Niobium',
    category: 'transition_metal',
    electronConfiguration: '[Kr] 4d⁴ 5s¹',
    atomicWeight: 92.90638,
    state: 'solid',
    density: 8.57000,
    meltingPoint: 2750.00,
    boilingPoint: 5017.00,
    electronegativity: 1.60,
    discoveredBy: 'Charles Hatchett',
    yearDiscovered: 1801,
    description: 'Le niobium est utilisé dans les supraconducteurs et les alliages.',
    uses: 'Supraconducteurs, aciers spéciaux, joaillerie.',
    color: '#f07178',
    period: 5,
    gridRow: 5,
    gridColumn: 5,
    isotopes: [
      {
            "massNumber": 93,
            "symbol": "⁹³Nb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      }
]
  },
  {
    id: 42,
    atomicNumber: 42,
    symbol: 'Mo',
    name: 'Molybdène',
    category: 'transition_metal',
    electronConfiguration: '[Kr] 4d⁵ 5s¹',
    atomicWeight: 95.96000,
    state: 'solid',
    density: 10.22000,
    meltingPoint: 2896.00,
    boilingPoint: 4912.00,
    electronegativity: 2.16,
    discoveredBy: 'Carl Wilhelm Scheele',
    yearDiscovered: 1778,
    description: 'Le molybdène a un point de fusion élevé et est utilisé dans les alliages.',
    uses: 'Aciers à haute résistance, catalyseurs, lubrifiants.',
    color: '#f07178',
    period: 5,
    gridRow: 5,
    gridColumn: 6,
    isotopes: [
      {
            "massNumber": 92,
            "symbol": "⁹²Mo",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 14.84,
            "isStable": true
      },
      {
            "massNumber": 94,
            "symbol": "⁹⁴Mo",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 9.25,
            "isStable": true
      },
      {
            "massNumber": 95,
            "symbol": "⁹⁵Mo",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 15.92,
            "isStable": true
      },
      {
            "massNumber": 96,
            "symbol": "⁹⁶Mo",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 16.68,
            "isStable": true
      },
      {
            "massNumber": 97,
            "symbol": "⁹⁷Mo",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 9.55,
            "isStable": true
      },
      {
            "massNumber": 98,
            "symbol": "⁹⁸Mo",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 24.13,
            "isStable": true
      },
      {
            "massNumber": 100,
            "symbol": "¹⁰⁰Mo",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 9.63,
            "isStable": true
      }
]
  },
  {
    id: 43,
    atomicNumber: 43,
    symbol: 'Tc',
    name: 'Technétium',
    category: 'transition_metal',
    electronConfiguration: '[Kr] 4d⁵ 5s²',
    atomicWeight: 98.00000,
    state: 'solid',
    density: 11.00000,
    meltingPoint: 2430.00,
    boilingPoint: 4538.00,
    discoveredBy: 'Carlo Perrier',
    yearDiscovered: 1937,
    description: 'Le technétium est le premier élément synthétique découvert.',
    uses: 'Imagerie médicale, inhibiteur de corrosion.',
    color: '#f07178',
    period: 5,
    gridRow: 5,
    gridColumn: 7,
    isotopes: [
      {
            "massNumber": 97,
            "symbol": "⁹⁷Tc",
            "halfLife": "4.2 millions d'années",
            "halfLifeSeconds": 133000000000000,
            "decayMode": [
                  "gamma"
            ],
            "isStable": false
      },
      {
            "massNumber": 98,
            "symbol": "⁹⁸Tc",
            "halfLife": "4.2 millions d'années",
            "halfLifeSeconds": 133000000000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      },
      {
            "massNumber": 99,
            "symbol": "⁹⁹Tc",
            "halfLife": "211000 ans",
            "halfLifeSeconds": 6660000000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 44,
    atomicNumber: 44,
    symbol: 'Ru',
    name: 'Ruthénium',
    category: 'transition_metal',
    electronConfiguration: '[Kr] 4d⁷ 5s¹',
    atomicWeight: 101.07000,
    state: 'solid',
    density: 12.37000,
    meltingPoint: 2607.00,
    boilingPoint: 4423.00,
    electronegativity: 2.20,
    discoveredBy: 'Karl Ernst Claus',
    yearDiscovered: 1844,
    description: 'Le ruthénium est un métal du groupe du platine rare et résistant.',
    uses: 'Catalyseurs, contacts électriques, durcissement du platine.',
    color: '#f07178',
    period: 5,
    gridRow: 5,
    gridColumn: 8,
    isotopes: [
      {
            "massNumber": 96,
            "symbol": "⁹⁶Ru",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 5.54,
            "isStable": true
      },
      {
            "massNumber": 98,
            "symbol": "⁹⁸Ru",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 1.87,
            "isStable": true
      },
      {
            "massNumber": 99,
            "symbol": "⁹⁹Ru",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 12.76,
            "isStable": true
      },
      {
            "massNumber": 100,
            "symbol": "¹⁰⁰Ru",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 12.6,
            "isStable": true
      },
      {
            "massNumber": 101,
            "symbol": "¹⁰¹Ru",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 17.06,
            "isStable": true
      },
      {
            "massNumber": 102,
            "symbol": "¹⁰²Ru",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 31.55,
            "isStable": true
      },
      {
            "massNumber": 104,
            "symbol": "¹⁰⁴Ru",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 18.62,
            "isStable": true
      }
]
  },
  {
    id: 45,
    atomicNumber: 45,
    symbol: 'Rh',
    name: 'Rhodium',
    category: 'transition_metal',
    electronConfiguration: '[Kr] 4d⁸ 5s¹',
    atomicWeight: 102.90550,
    state: 'solid',
    density: 12.41000,
    meltingPoint: 2237.00,
    boilingPoint: 3968.00,
    electronegativity: 2.28,
    discoveredBy: 'William Hyde Wollaston',
    yearDiscovered: 1803,
    description: 'Le rhodium est un métal précieux utilisé en catalyse.',
    uses: 'Catalyseurs automobiles, joaillerie, miroirs.',
    color: '#f07178',
    period: 5,
    gridRow: 5,
    gridColumn: 9,
    isotopes: [
      {
            "massNumber": 103,
            "symbol": "¹⁰³Rh",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      }
]
  },
  {
    id: 46,
    atomicNumber: 46,
    symbol: 'Pd',
    name: 'Palladium',
    category: 'transition_metal',
    electronConfiguration: '[Kr] 4d¹⁰',
    atomicWeight: 106.42000,
    state: 'solid',
    density: 12.02000,
    meltingPoint: 1828.05,
    boilingPoint: 3236.00,
    electronegativity: 2.20,
    discoveredBy: 'William Hyde Wollaston',
    yearDiscovered: 1803,
    description: 'Le palladium peut absorber de grandes quantités d\'hydrogène.',
    uses: 'Catalyseurs, électronique, joaillerie, dentisterie.',
    color: '#f07178',
    period: 5,
    gridRow: 5,
    gridColumn: 10,
    isotopes: [
      {
            "massNumber": 102,
            "symbol": "¹⁰²Pd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 1.02,
            "isStable": true
      },
      {
            "massNumber": 104,
            "symbol": "¹⁰⁴Pd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 11.14,
            "isStable": true
      },
      {
            "massNumber": 105,
            "symbol": "¹⁰⁵Pd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 22.33,
            "isStable": true
      },
      {
            "massNumber": 106,
            "symbol": "¹⁰⁶Pd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 27.33,
            "isStable": true
      },
      {
            "massNumber": 108,
            "symbol": "¹⁰⁸Pd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 26.46,
            "isStable": true
      },
      {
            "massNumber": 110,
            "symbol": "¹¹⁰Pd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 11.72,
            "isStable": true
      }
]
  },
  {
    id: 47,
    atomicNumber: 47,
    symbol: 'Ag',
    name: 'Argent',
    category: 'transition_metal',
    electronConfiguration: '[Kr] 4d¹⁰ 5s¹',
    atomicWeight: 107.86820,
    state: 'solid',
    density: 10.49000,
    meltingPoint: 1234.93,
    boilingPoint: 2435.00,
    electronegativity: 1.93,
    discoveredBy: 'Anciens',
    description: "L'argent est connu pour sa conductivité électrique et sa résistance à la corrosion.",
    uses: 'Bijoux, photographie, électronique, monnaie.',
    color: '#f07178',
    period: 5,
    gridRow: 5,
    gridColumn: 11,
    isotopes: [
      {
            "massNumber": 107,
            "symbol": "¹⁰⁷Ag",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 51.84,
            "isStable": true
      },
      {
            "massNumber": 109,
            "symbol": "¹⁰⁹Ag",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 48.16,
            "isStable": true
      }
]
  },
  {
    id: 48,
    atomicNumber: 48,
    symbol: 'Cd',
    name: 'Cadmium',
    category: 'transition_metal',
    electronConfiguration: '[Kr] 4d¹⁰ 5s²',
    atomicWeight: 112.41100,
    state: 'solid',
    density: 8.69000,
    meltingPoint: 594.22,
    boilingPoint: 1040.00,
    electronegativity: 1.69,
    discoveredBy: 'Karl Samuel Leberecht Hermann',
    yearDiscovered: 1817,
    description: 'Le cadmium est toxique et utilisé dans les batteries.',
    uses: 'Batteries Ni-Cd, pigments, placage, stabilisateurs pour PVC.',
    color: '#f07178',
    period: 5,
    gridRow: 5,
    gridColumn: 12,
    isotopes: [
      {
            "massNumber": 106,
            "symbol": "¹⁰⁶Cd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 1.25,
            "isStable": true
      },
      {
            "massNumber": 108,
            "symbol": "¹⁰⁸Cd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.89,
            "isStable": true
      },
      {
            "massNumber": 110,
            "symbol": "¹¹⁰Cd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 12.49,
            "isStable": true
      },
      {
            "massNumber": 111,
            "symbol": "¹¹¹Cd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 12.8,
            "isStable": true
      },
      {
            "massNumber": 112,
            "symbol": "¹¹²Cd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 24.13,
            "isStable": true
      },
      {
            "massNumber": 114,
            "symbol": "¹¹⁴Cd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 28.73,
            "isStable": true
      }
]
  },
  {
    id: 49,
    atomicNumber: 49,
    symbol: 'In',
    name: 'Indium',
    category: 'post_transition_metal',
    electronConfiguration: '[Kr] 4d¹⁰ 5s² 5p¹',
    atomicWeight: 114.81800,
    state: 'solid',
    density: 7.31000,
    meltingPoint: 429.75,
    boilingPoint: 2345.00,
    electronegativity: 1.78,
    discoveredBy: 'Ferdinand Reich',
    yearDiscovered: 1863,
    description: 'L\'indium est mou et utilisé dans les écrans tactiles.',
    uses: 'Écrans LCD, alliages, semi-conducteurs, soudure.',
    color: '#89ddff',
    period: 5,
    gridRow: 5,
    gridColumn: 13,
    isotopes: [
      {
            "massNumber": 113,
            "symbol": "¹¹³In",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 4.29,
            "isStable": true
      },
      {
            "massNumber": 115,
            "symbol": "¹¹⁵In",
            "halfLife": "4.41e14 ans",
            "halfLifeSeconds": 1.39e+22,
            "decayMode": [
                  "beta-"
            ],
            "naturalAbundance": 95.71,
            "isStable": false
      }
]
  },
  {
    id: 50,
    atomicNumber: 50,
    symbol: 'Sn',
    name: 'Étain',
    category: 'post_transition_metal',
    electronConfiguration: '[Kr] 4d¹⁰ 5s² 5p²',
    atomicWeight: 118.71000,
    state: 'solid',
    density: 7.26500,
    meltingPoint: 505.08,
    boilingPoint: 2875.00,
    electronegativity: 1.96,
    discoveredBy: 'Anciens',
    description: 'L\'étain est utilisé pour protéger d\'autres métaux de la corrosion.',
    uses: 'Boîtes de conserve, soudure, alliages (bronze), verre flotté.',
    color: '#89ddff',
    period: 5,
    gridRow: 5,
    gridColumn: 14,
    isotopes: [
      {
            "massNumber": 112,
            "symbol": "¹¹²Sn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.97,
            "isStable": true
      },
      {
            "massNumber": 114,
            "symbol": "¹¹⁴Sn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.66,
            "isStable": true
      },
      {
            "massNumber": 115,
            "symbol": "¹¹⁵Sn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.34,
            "isStable": true
      },
      {
            "massNumber": 116,
            "symbol": "¹¹⁶Sn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 14.54,
            "isStable": true
      },
      {
            "massNumber": 117,
            "symbol": "¹¹⁷Sn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 7.68,
            "isStable": true
      },
      {
            "massNumber": 118,
            "symbol": "¹¹⁸Sn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 24.22,
            "isStable": true
      },
      {
            "massNumber": 119,
            "symbol": "¹¹⁹Sn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 8.59,
            "isStable": true
      },
      {
            "massNumber": 120,
            "symbol": "¹²⁰Sn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 32.58,
            "isStable": true
      },
      {
            "massNumber": 122,
            "symbol": "¹²²Sn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 4.63,
            "isStable": true
      },
      {
            "massNumber": 124,
            "symbol": "¹²⁴Sn",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 5.79,
            "isStable": true
      }
]
  },
  {
    id: 51,
    atomicNumber: 51,
    symbol: 'Sb',
    name: 'Antimoine',
    category: 'metalloid',
    electronConfiguration: '[Kr] 4d¹⁰ 5s² 5p³',
    atomicWeight: 121.76000,
    state: 'solid',
    density: 6.68500,
    meltingPoint: 903.78,
    boilingPoint: 1860.00,
    electronegativity: 2.05,
    discoveredBy: 'Anciens',
    description: 'L\'antimoine est un métalloïde utilisé dans les retardateurs de flamme.',
    uses: 'Retardateurs de flamme, alliages de plomb, semi-conducteurs.',
    color: '#c3e88d',
    period: 5,
    gridRow: 5,
    gridColumn: 15,
    isotopes: [
      {
            "massNumber": 121,
            "symbol": "¹²¹Sb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 57.21,
            "isStable": true
      },
      {
            "massNumber": 123,
            "symbol": "¹²³Sb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 42.79,
            "isStable": true
      }
]
  },
  {
    id: 52,
    atomicNumber: 52,
    symbol: 'Te',
    name: 'Tellure',
    category: 'metalloid',
    electronConfiguration: '[Kr] 4d¹⁰ 5s² 5p⁴',
    atomicWeight: 127.60000,
    state: 'solid',
    density: 6.23200,
    meltingPoint: 722.66,
    boilingPoint: 1261.00,
    electronegativity: 2.10,
    discoveredBy: 'Franz-Joseph Müller von Reichenstein',
    yearDiscovered: 1782,
    description: 'Le tellure est rare et utilisé en métallurgie.',
    uses: 'Alliages, cellules solaires, thermoélectricité, optique.',
    color: '#c3e88d',
    period: 5,
    gridRow: 5,
    gridColumn: 16,
    isotopes: [
      {
            "massNumber": 120,
            "symbol": "¹²⁰Te",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.09,
            "isStable": true
      },
      {
            "massNumber": 122,
            "symbol": "¹²²Te",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 2.55,
            "isStable": true
      },
      {
            "massNumber": 123,
            "symbol": "¹²³Te",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.89,
            "isStable": true
      },
      {
            "massNumber": 124,
            "symbol": "¹²⁴Te",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 4.74,
            "isStable": true
      },
      {
            "massNumber": 125,
            "symbol": "¹²⁵Te",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 7.07,
            "isStable": true
      },
      {
            "massNumber": 126,
            "symbol": "¹²⁶Te",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 18.84,
            "isStable": true
      },
      {
            "massNumber": 128,
            "symbol": "¹²⁸Te",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 31.74,
            "isStable": true
      },
      {
            "massNumber": 130,
            "symbol": "¹³⁰Te",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 34.08,
            "isStable": true
      }
]
  },
  {
    id: 53,
    atomicNumber: 53,
    symbol: 'I',
    name: 'Iode',
    category: 'halogen',
    electronConfiguration: '[Kr] 4d¹⁰ 5s² 5p⁵',
    atomicWeight: 126.90447,
    state: 'solid',
    density: 4.93300,
    meltingPoint: 386.85,
    boilingPoint: 457.40,
    electronegativity: 2.66,
    discoveredBy: 'Bernard Courtois',
    yearDiscovered: 1811,
    description: 'L\'iode est essentiel pour la thyroïde humaine.',
    uses: 'Désinfectant, supplément nutritionnel, photographie, catalyseurs.',
    color: '#ffcb6b',
    period: 5,
    gridRow: 5,
    gridColumn: 17,
    isotopes: [
      {
            "massNumber": 127,
            "symbol": "¹²⁷I",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      },
      {
            "massNumber": 129,
            "symbol": "¹²⁹I",
            "halfLife": "15.7 millions d'années",
            "halfLifeSeconds": 495000000000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      },
      {
            "massNumber": 131,
            "symbol": "¹³¹I",
            "halfLife": "8.02 jours",
            "halfLifeSeconds": 693173,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 54,
    atomicNumber: 54,
    symbol: 'Xe',
    name: 'Xénon',
    category: 'noble_gas',
    electronConfiguration: '[Kr] 4d¹⁰ 5s² 5p⁶',
    atomicWeight: 131.29300,
    state: 'gas',
    density: 5.89400,
    meltingPoint: 161.40,
    boilingPoint: 165.03,
    discoveredBy: 'William Ramsay',
    yearDiscovered: 1898,
    description: 'Le xénon est utilisé dans les lampes et l\'anesthésie.',
    uses: 'Lampes HID, propulsion ionique, anesthésie, imagerie médicale.',
    color: '#c792ea',
    period: 5,
    gridRow: 5,
    gridColumn: 18,
    isotopes: [
      {
            "massNumber": 124,
            "symbol": "¹²⁴Xe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.09,
            "isStable": true
      },
      {
            "massNumber": 126,
            "symbol": "¹²⁶Xe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.09,
            "isStable": true
      },
      {
            "massNumber": 128,
            "symbol": "¹²⁸Xe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 1.92,
            "isStable": true
      },
      {
            "massNumber": 129,
            "symbol": "¹²⁹Xe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 26.44,
            "isStable": true
      },
      {
            "massNumber": 130,
            "symbol": "¹³⁰Xe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 4.08,
            "isStable": true
      },
      {
            "massNumber": 131,
            "symbol": "¹³¹Xe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 21.18,
            "isStable": true
      },
      {
            "massNumber": 132,
            "symbol": "¹³²Xe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 26.89,
            "isStable": true
      },
      {
            "massNumber": 134,
            "symbol": "¹³⁴Xe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 10.44,
            "isStable": true
      },
      {
            "massNumber": 136,
            "symbol": "¹³⁶Xe",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 8.87,
            "isStable": true
      }
]
  },
  {
    id: 55,
    atomicNumber: 55,
    symbol: 'Cs',
    name: 'Césium',
    category: 'alkali_metal',
    electronConfiguration: '[Xe] 6s¹',
    atomicWeight: 132.90545,
    state: 'solid',
    density: 1.87300,
    meltingPoint: 301.59,
    boilingPoint: 944.00,
    electronegativity: 0.79,
    discoveredBy: 'Robert Bunsen',
    yearDiscovered: 1860,
    description: 'Le césium est le plus réactif des métaux alcalins.',
    uses: 'Horloges atomiques, catalyseurs, cellules photoélectriques.',
    color: '#ff5370',
    period: 6,
    gridRow: 6,
    gridColumn: 1,
    isotopes: [
      {
            "massNumber": 133,
            "symbol": "¹³³Cs",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      },
      {
            "massNumber": 134,
            "symbol": "¹³⁴Cs",
            "halfLife": "2.07 ans",
            "halfLifeSeconds": 65300000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      },
      {
            "massNumber": 137,
            "symbol": "¹³⁷Cs",
            "halfLife": "30.17 ans",
            "halfLifeSeconds": 952000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 56,
    atomicNumber: 56,
    symbol: 'Ba',
    name: 'Baryum',
    category: 'alkaline_earth_metal',
    electronConfiguration: '[Xe] 6s²',
    atomicWeight: 137.32700,
    state: 'solid',
    density: 3.59400,
    meltingPoint: 1000.00,
    boilingPoint: 2170.00,
    electronegativity: 0.89,
    discoveredBy: 'Humphry Davy',
    yearDiscovered: 1808,
    description: 'Le baryum est utilisé dans les rayons X et les feux d\'artifice.',
    uses: 'Imagerie médicale, verre, feux d\'artifice (vert), huiles de forage.',
    color: '#f78c6c',
    period: 6,
    gridRow: 6,
    gridColumn: 2,
    isotopes: [
      {
            "massNumber": 130,
            "symbol": "¹³⁰Ba",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.11,
            "isStable": true
      },
      {
            "massNumber": 132,
            "symbol": "¹³²Ba",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.1,
            "isStable": true
      },
      {
            "massNumber": 134,
            "symbol": "¹³⁴Ba",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 2.42,
            "isStable": true
      },
      {
            "massNumber": 135,
            "symbol": "¹³⁵Ba",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 6.59,
            "isStable": true
      },
      {
            "massNumber": 136,
            "symbol": "¹³⁶Ba",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 7.85,
            "isStable": true
      },
      {
            "massNumber": 137,
            "symbol": "¹³⁷Ba",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 11.23,
            "isStable": true
      },
      {
            "massNumber": 138,
            "symbol": "¹³⁸Ba",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 71.7,
            "isStable": true
      }
]
  },
  {
    id: 57,
    atomicNumber: 57,
    symbol: 'La',
    name: 'Lanthane',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 5d¹ 6s²',
    atomicWeight: 138.90547,
    state: 'solid',
    density: 6.14500,
    meltingPoint: 1193.00,
    boilingPoint: 3737.00,
    electronegativity: 1.10,
    discoveredBy: 'Carl Gustaf Mosander',
    yearDiscovered: 1839,
    description: 'Le lanthane est le premier des lanthanides.',
    uses: 'Batteries hybrides, catalyseurs, verre optique, alliages.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 3,
    isotopes: [
      {
            "massNumber": 138,
            "symbol": "¹³⁸La",
            "halfLife": "102 milliards d'années",
            "halfLifeSeconds": 3220000000000000000,
            "decayMode": [
                  "gamma"
            ],
            "naturalAbundance": 0.09,
            "isStable": false
      },
      {
            "massNumber": 139,
            "symbol": "¹³⁹La",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 99.91,
            "isStable": true
      }
]
  },
  {
    id: 58,
    atomicNumber: 58,
    symbol: 'Ce',
    name: 'Cérium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f¹ 5d¹ 6s²',
    atomicWeight: 140.11600,
    state: 'solid',
    density: 6.77000,
    meltingPoint: 1068.00,
    boilingPoint: 3716.00,
    electronegativity: 1.12,
    discoveredBy: 'Jöns Jacob Berzelius',
    yearDiscovered: 1803,
    description: 'Le cérium est le plus abondant des terres rares.',
    uses: 'Catalyseurs, pierres à briquet, polissage du verre.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 4,
    isotopes: [
      {
            "massNumber": 136,
            "symbol": "¹³⁶Ce",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.19,
            "isStable": true
      },
      {
            "massNumber": 138,
            "symbol": "¹³⁸Ce",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.25,
            "isStable": true
      },
      {
            "massNumber": 140,
            "symbol": "¹⁴⁰Ce",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 88.45,
            "isStable": true
      },
      {
            "massNumber": 142,
            "symbol": "¹⁴²Ce",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 11.11,
            "isStable": true
      }
]
  },
  {
    id: 59,
    atomicNumber: 59,
    symbol: 'Pr',
    name: 'Praséodyme',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f³ 6s²',
    atomicWeight: 140.90765,
    state: 'solid',
    density: 6.77300,
    meltingPoint: 1208.00,
    boilingPoint: 3793.00,
    electronegativity: 1.13,
    discoveredBy: 'Carl Auer von Welsbach',
    yearDiscovered: 1885,
    description: 'Le praséodyme est utilisé pour les lunettes de soudeur.',
    uses: 'Aimants, lunettes de soudeur, alliages, colorant pour verre.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 5,
    isotopes: [
      {
            "massNumber": 141,
            "symbol": "¹⁴¹Pr",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      }
]
  },
  {
    id: 60,
    atomicNumber: 60,
    symbol: 'Nd',
    name: 'Néodyme',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f⁴ 6s²',
    atomicWeight: 144.24200,
    state: 'solid',
    density: 7.00700,
    meltingPoint: 1297.00,
    boilingPoint: 3347.00,
    electronegativity: 1.14,
    discoveredBy: 'Carl Auer von Welsbach',
    yearDiscovered: 1885,
    description: 'Le néodyme crée les aimants permanents les plus puissants.',
    uses: 'Aimants puissants, lasers, colorant pour verre et céramique.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 6,
    isotopes: [
      {
            "massNumber": 142,
            "symbol": "¹⁴²Nd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 27.13,
            "isStable": true
      },
      {
            "massNumber": 143,
            "symbol": "¹⁴³Nd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 12.18,
            "isStable": true
      },
      {
            "massNumber": 144,
            "symbol": "¹⁴⁴Nd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 23.8,
            "isStable": true
      },
      {
            "massNumber": 145,
            "symbol": "¹⁴⁵Nd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 8.3,
            "isStable": true
      },
      {
            "massNumber": 146,
            "symbol": "¹⁴⁶Nd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 17.19,
            "isStable": true
      },
      {
            "massNumber": 148,
            "symbol": "¹⁴⁸Nd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 5.76,
            "isStable": true
      },
      {
            "massNumber": 150,
            "symbol": "¹⁵⁰Nd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 5.64,
            "isStable": true
      }
]
  },
  {
    id: 61,
    atomicNumber: 61,
    symbol: 'Pm',
    name: 'Prométhium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f⁵ 6s²',
    atomicWeight: 145.00000,
    state: 'solid',
    density: 7.26000,
    meltingPoint: 1315.00,
    boilingPoint: 3273.00,
    discoveredBy: 'Jacob A. Marinsky',
    yearDiscovered: 1945,
    description: 'Le prométhium est radioactif et n\'existe pas naturellement.',
    uses: 'Batteries nucléaires, peintures luminescentes, recherche.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 7,
    isotopes: [
      {
            "massNumber": 145,
            "symbol": "¹⁴⁵Pm",
            "halfLife": "17.7 ans",
            "halfLifeSeconds": 559000000,
            "decayMode": [
                  "gamma"
            ],
            "isStable": false
      },
      {
            "massNumber": 147,
            "symbol": "¹⁴⁷Pm",
            "halfLife": "2.62 ans",
            "halfLifeSeconds": 82700000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 62,
    atomicNumber: 62,
    symbol: 'Sm',
    name: 'Samarium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f⁶ 6s²',
    atomicWeight: 150.36000,
    state: 'solid',
    density: 7.52000,
    meltingPoint: 1345.00,
    boilingPoint: 2067.00,
    electronegativity: 1.17,
    discoveredBy: 'Paul-Émile Lecoq de Boisbaudran',
    yearDiscovered: 1879,
    description: 'Le samarium est utilisé dans les aimants et les lasers.',
    uses: 'Aimants permanents, lasers, absorbe les neutrons, catalyseurs.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 8,
    isotopes: [
      {
            "massNumber": 144,
            "symbol": "¹⁴⁴Sm",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 3.07,
            "isStable": true
      },
      {
            "massNumber": 147,
            "symbol": "¹⁴⁷Sm",
            "halfLife": "106 milliards d'années",
            "halfLifeSeconds": 3350000000000000000,
            "decayMode": [
                  "alpha"
            ],
            "naturalAbundance": 14.99,
            "isStable": false
      },
      {
            "massNumber": 148,
            "symbol": "¹⁴⁸Sm",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 11.24,
            "isStable": true
      },
      {
            "massNumber": 149,
            "symbol": "¹⁴⁹Sm",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 13.82,
            "isStable": true
      },
      {
            "massNumber": 150,
            "symbol": "¹⁵⁰Sm",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 7.38,
            "isStable": true
      },
      {
            "massNumber": 152,
            "symbol": "¹⁵²Sm",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 26.75,
            "isStable": true
      },
      {
            "massNumber": 154,
            "symbol": "¹⁵⁴Sm",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 22.75,
            "isStable": true
      }
]
  },
  {
    id: 63,
    atomicNumber: 63,
    symbol: 'Eu',
    name: 'Europium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f⁷ 6s²',
    atomicWeight: 151.96400,
    state: 'solid',
    density: 5.24300,
    meltingPoint: 1099.00,
    boilingPoint: 1802.00,
    electronegativity: 1.20,
    discoveredBy: 'Eugène-Anatole Demarçay',
    yearDiscovered: 1901,
    description: 'L\'europium est utilisé comme phosphore rouge dans les écrans.',
    uses: 'Phosphores pour écrans, billets de banque (anti-contrefaçon), lasers.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 9,
    isotopes: [
      {
            "massNumber": 151,
            "symbol": "¹⁵¹Eu",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 47.81,
            "isStable": true
      },
      {
            "massNumber": 153,
            "symbol": "¹⁵³Eu",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 52.19,
            "isStable": true
      }
]
  },
  {
    id: 64,
    atomicNumber: 64,
    symbol: 'Gd',
    name: 'Gadolinium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f⁷ 5d¹ 6s²',
    atomicWeight: 157.25000,
    state: 'solid',
    density: 7.89500,
    meltingPoint: 1585.00,
    boilingPoint: 3546.00,
    electronegativity: 1.20,
    discoveredBy: 'Jean Charles Galissard de Marignac',
    yearDiscovered: 1880,
    description: 'Le gadolinium a des propriétés magnétiques exceptionnelles.',
    uses: 'IRM (agent de contraste), aimants, réacteurs nucléaires.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 10,
    isotopes: [
      {
            "massNumber": 152,
            "symbol": "¹⁵²Gd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.2,
            "isStable": true
      },
      {
            "massNumber": 154,
            "symbol": "¹⁵⁴Gd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 2.18,
            "isStable": true
      },
      {
            "massNumber": 155,
            "symbol": "¹⁵⁵Gd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 14.8,
            "isStable": true
      },
      {
            "massNumber": 156,
            "symbol": "¹⁵⁶Gd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 20.47,
            "isStable": true
      },
      {
            "massNumber": 157,
            "symbol": "¹⁵⁷Gd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 15.65,
            "isStable": true
      },
      {
            "massNumber": 158,
            "symbol": "¹⁵⁸Gd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 24.84,
            "isStable": true
      },
      {
            "massNumber": 160,
            "symbol": "¹⁶⁰Gd",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 21.86,
            "isStable": true
      }
]
  },
  {
    id: 65,
    atomicNumber: 65,
    symbol: 'Tb',
    name: 'Terbium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f⁹ 6s²',
    atomicWeight: 158.92535,
    state: 'solid',
    density: 8.22900,
    meltingPoint: 1629.00,
    boilingPoint: 3503.00,
    electronegativity: 1.20,
    discoveredBy: 'Carl Gustaf Mosander',
    yearDiscovered: 1843,
    description: 'Le terbium est utilisé dans les phosphores verts.',
    uses: 'Phosphores verts, aimants, capteurs, lasers.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 11,
    isotopes: [
      {
            "massNumber": 159,
            "symbol": "¹⁵⁹Tb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      }
]
  },
  {
    id: 66,
    atomicNumber: 66,
    symbol: 'Dy',
    name: 'Dysprosium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f¹⁰ 6s²',
    atomicWeight: 162.50000,
    state: 'solid',
    density: 8.55100,
    meltingPoint: 1680.00,
    boilingPoint: 2840.00,
    electronegativity: 1.22,
    discoveredBy: 'Paul-Émile Lecoq de Boisbaudran',
    yearDiscovered: 1886,
    description: 'Le dysprosium est utilisé dans les aimants haute température.',
    uses: 'Aimants haute température, lasers, disques durs, réacteurs.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 12,
    isotopes: [
      {
            "massNumber": 156,
            "symbol": "¹⁵⁶Dy",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.06,
            "isStable": true
      },
      {
            "massNumber": 158,
            "symbol": "¹⁵⁸Dy",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.1,
            "isStable": true
      },
      {
            "massNumber": 160,
            "symbol": "¹⁶⁰Dy",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 2.34,
            "isStable": true
      },
      {
            "massNumber": 161,
            "symbol": "¹⁶¹Dy",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 18.91,
            "isStable": true
      },
      {
            "massNumber": 162,
            "symbol": "¹⁶²Dy",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 25.51,
            "isStable": true
      },
      {
            "massNumber": 163,
            "symbol": "¹⁶³Dy",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 24.9,
            "isStable": true
      },
      {
            "massNumber": 164,
            "symbol": "¹⁶⁴Dy",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 28.18,
            "isStable": true
      }
]
  },
  {
    id: 67,
    atomicNumber: 67,
    symbol: 'Ho',
    name: 'Holmium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f¹¹ 6s²',
    atomicWeight: 164.93032,
    state: 'solid',
    density: 8.79500,
    meltingPoint: 1734.00,
    boilingPoint: 2993.00,
    electronegativity: 1.23,
    discoveredBy: 'Per Teodor Cleve',
    yearDiscovered: 1878,
    description: 'Le holmium a le champ magnétique le plus élevé.',
    uses: 'Lasers médicaux, aimants, contrôle des réactions nucléaires.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 13,
    isotopes: [
      {
            "massNumber": 165,
            "symbol": "¹⁶⁵Ho",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      }
]
  },
  {
    id: 68,
    atomicNumber: 68,
    symbol: 'Er',
    name: 'Erbium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f¹² 6s²',
    atomicWeight: 167.25900,
    state: 'solid',
    density: 9.06600,
    meltingPoint: 1802.00,
    boilingPoint: 3141.00,
    electronegativity: 1.24,
    discoveredBy: 'Carl Gustaf Mosander',
    yearDiscovered: 1843,
    description: 'L\'erbium est utilisé dans les amplificateurs à fibre optique.',
    uses: 'Fibres optiques, lasers, colorant pour verre et céramique.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 14,
    isotopes: [
      {
            "massNumber": 162,
            "symbol": "¹⁶²Er",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.14,
            "isStable": true
      },
      {
            "massNumber": 164,
            "symbol": "¹⁶⁴Er",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 1.61,
            "isStable": true
      },
      {
            "massNumber": 166,
            "symbol": "¹⁶⁶Er",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 33.61,
            "isStable": true
      },
      {
            "massNumber": 167,
            "symbol": "¹⁶⁷Er",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 22.93,
            "isStable": true
      },
      {
            "massNumber": 168,
            "symbol": "¹⁶⁸Er",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 26.78,
            "isStable": true
      },
      {
            "massNumber": 170,
            "symbol": "¹⁷⁰Er",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 14.93,
            "isStable": true
      }
]
  },
  {
    id: 69,
    atomicNumber: 69,
    symbol: 'Tm',
    name: 'Thulium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f¹³ 6s²',
    atomicWeight: 168.93421,
    state: 'solid',
    density: 9.32100,
    meltingPoint: 1818.00,
    boilingPoint: 2223.00,
    electronegativity: 1.25,
    discoveredBy: 'Per Teodor Cleve',
    yearDiscovered: 1879,
    description: 'Le thulium est la terre rare la moins abondante.',
    uses: 'Lasers médicaux, rayons X portables, alliages.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 15,
    isotopes: [
      {
            "massNumber": 169,
            "symbol": "¹⁶⁹Tm",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      },
      {
            "massNumber": 170,
            "symbol": "¹⁷⁰Tm",
            "halfLife": "128.6 jours",
            "halfLifeSeconds": 11110000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 70,
    atomicNumber: 70,
    symbol: 'Yb',
    name: 'Ytterbium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f¹⁴ 6s²',
    atomicWeight: 173.05400,
    state: 'solid',
    density: 6.96500,
    meltingPoint: 1097.00,
    boilingPoint: 1469.00,
    electronegativity: 1.10,
    discoveredBy: 'Jean Charles Galissard de Marignac',
    yearDiscovered: 1878,
    description: 'L\'ytterbium est utilisé en horlogerie atomique.',
    uses: 'Horloges atomiques, lasers, alliages d\'acier inoxydable.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 16,
    isotopes: [
      {
            "massNumber": 168,
            "symbol": "¹⁶⁸Yb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.13,
            "isStable": true
      },
      {
            "massNumber": 170,
            "symbol": "¹⁷⁰Yb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 3.04,
            "isStable": true
      },
      {
            "massNumber": 171,
            "symbol": "¹⁷¹Yb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 14.28,
            "isStable": true
      },
      {
            "massNumber": 172,
            "symbol": "¹⁷²Yb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 21.83,
            "isStable": true
      },
      {
            "massNumber": 173,
            "symbol": "¹⁷³Yb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 16.13,
            "isStable": true
      },
      {
            "massNumber": 174,
            "symbol": "¹⁷⁴Yb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 31.83,
            "isStable": true
      },
      {
            "massNumber": 176,
            "symbol": "¹⁷⁶Yb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 12.76,
            "isStable": true
      }
]
  },
  {
    id: 71,
    atomicNumber: 71,
    symbol: 'Lu',
    name: 'Lutécium',
    category: 'lanthanide',
    electronConfiguration: '[Xe] 4f¹⁴ 5d¹ 6s²',
    atomicWeight: 174.96680,
    state: 'solid',
    density: 9.84100,
    meltingPoint: 1925.00,
    boilingPoint: 3675.00,
    electronegativity: 1.27,
    discoveredBy: 'Georges Urbain',
    yearDiscovered: 1907,
    description: 'Le lutécium est le dernier des lanthanides.',
    uses: 'Catalyseurs de craquage pétrolier, scintillateurs, recherche.',
    color: '#c792ea',
    period: 6,
    gridRow: 9,
    gridColumn: 17,
    isotopes: [
      {
            "massNumber": 175,
            "symbol": "¹⁷⁵Lu",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 97.41,
            "isStable": true
      },
      {
            "massNumber": 176,
            "symbol": "¹⁷⁶Lu",
            "halfLife": "37.8 milliards d'années",
            "halfLifeSeconds": 1190000000000000000,
            "decayMode": [
                  "beta-"
            ],
            "naturalAbundance": 2.59,
            "isStable": false
      }
]
  },
  {
    id: 72,
    atomicNumber: 72,
    symbol: 'Hf',
    name: 'Hafnium',
    category: 'transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d² 6s²',
    atomicWeight: 178.49000,
    state: 'solid',
    density: 13.31000,
    meltingPoint: 2506.00,
    boilingPoint: 4876.00,
    electronegativity: 1.30,
    discoveredBy: 'Dirk Coster',
    yearDiscovered: 1923,
    description: 'Le hafnium absorbe les neutrons et est utilisé dans les réacteurs.',
    uses: 'Réacteurs nucléaires, superalliages, semi-conducteurs.',
    color: '#f07178',
    period: 6,
    gridRow: 6,
    gridColumn: 4,
    isotopes: [
      {
            "massNumber": 174,
            "symbol": "¹⁷⁴Hf",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.16,
            "isStable": true
      },
      {
            "massNumber": 176,
            "symbol": "¹⁷⁶Hf",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 5.26,
            "isStable": true
      },
      {
            "massNumber": 177,
            "symbol": "¹⁷⁷Hf",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 18.6,
            "isStable": true
      },
      {
            "massNumber": 178,
            "symbol": "¹⁷⁸Hf",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 27.28,
            "isStable": true
      },
      {
            "massNumber": 179,
            "symbol": "¹⁷⁹Hf",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 13.62,
            "isStable": true
      },
      {
            "massNumber": 180,
            "symbol": "¹⁸⁰Hf",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 35.08,
            "isStable": true
      }
]
  },
  {
    id: 73,
    atomicNumber: 73,
    symbol: 'Ta',
    name: 'Tantale',
    category: 'transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d³ 6s²',
    atomicWeight: 180.94788,
    state: 'solid',
    density: 16.65400,
    meltingPoint: 3290.00,
    boilingPoint: 5731.00,
    electronegativity: 1.50,
    discoveredBy: 'Anders Gustaf Ekeberg',
    yearDiscovered: 1802,
    description: 'Le tantale est très résistant à la corrosion.',
    uses: 'Condensateurs, implants chirurgicaux, superalliages, électronique.',
    color: '#f07178',
    period: 6,
    gridRow: 6,
    gridColumn: 5,
    isotopes: [
      {
            "massNumber": 180,
            "symbol": "¹⁸⁰Ta",
            "halfLife": "8.15 heures",
            "halfLifeSeconds": 29340,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      },
      {
            "massNumber": 181,
            "symbol": "¹⁸¹Ta",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 99.99,
            "isStable": true
      }
]
  },
  {
    id: 74,
    atomicNumber: 74,
    symbol: 'W',
    name: 'Tungstène',
    category: 'transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d⁴ 6s²',
    atomicWeight: 183.84000,
    state: 'solid',
    density: 19.25000,
    meltingPoint: 3695.00,
    boilingPoint: 5828.00,
    electronegativity: 2.36,
    discoveredBy: 'Carl Wilhelm Scheele',
    yearDiscovered: 1783,
    description: 'Le tungstène a le point de fusion le plus élevé de tous les métaux.',
    uses: 'Filaments d\'ampoules, alliages haute température, outils de coupe.',
    color: '#f07178',
    period: 6,
    gridRow: 6,
    gridColumn: 6,
    isotopes: [
      {
            "massNumber": 180,
            "symbol": "¹⁸⁰W",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.12,
            "isStable": true
      },
      {
            "massNumber": 182,
            "symbol": "¹⁸²W",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 26.5,
            "isStable": true
      },
      {
            "massNumber": 183,
            "symbol": "¹⁸³W",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 14.31,
            "isStable": true
      },
      {
            "massNumber": 184,
            "symbol": "¹⁸⁴W",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 30.64,
            "isStable": true
      },
      {
            "massNumber": 186,
            "symbol": "¹⁸⁶W",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 28.43,
            "isStable": true
      }
]
  },
  {
    id: 75,
    atomicNumber: 75,
    symbol: 'Re',
    name: 'Rhénium',
    category: 'transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d⁵ 6s²',
    atomicWeight: 186.20700,
    state: 'solid',
    density: 21.02000,
    meltingPoint: 3459.00,
    boilingPoint: 5869.00,
    electronegativity: 1.90,
    discoveredBy: 'Masataka Ogawa',
    yearDiscovered: 1908,
    description: 'Le rhénium est un métal rare avec un point de fusion très élevé.',
    uses: 'Superalliages pour moteurs à réaction, catalyseurs, thermocouples.',
    color: '#f07178',
    period: 6,
    gridRow: 6,
    gridColumn: 7,
    isotopes: [
      {
            "massNumber": 185,
            "symbol": "¹⁸⁵Re",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 37.4,
            "isStable": true
      },
      {
            "massNumber": 187,
            "symbol": "¹⁸⁷Re",
            "halfLife": "41.2 milliards d'années",
            "halfLifeSeconds": 1300000000000000000,
            "decayMode": [
                  "beta-"
            ],
            "naturalAbundance": 62.6,
            "isStable": false
      }
]
  },
  {
    id: 76,
    atomicNumber: 76,
    symbol: 'Os',
    name: 'Osmium',
    category: 'transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d⁶ 6s²',
    atomicWeight: 190.23000,
    state: 'solid',
    density: 22.59000,
    meltingPoint: 3306.00,
    boilingPoint: 5285.00,
    electronegativity: 2.20,
    discoveredBy: 'Smithson Tennant',
    yearDiscovered: 1803,
    description: 'L\'osmium est l\'élément le plus dense naturel.',
    uses: 'Alliages pour pointes de stylos, contacts électriques, catalyseurs.',
    color: '#f07178',
    period: 6,
    gridRow: 6,
    gridColumn: 8,
    isotopes: [
      {
            "massNumber": 184,
            "symbol": "¹⁸⁴Os",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.02,
            "isStable": true
      },
      {
            "massNumber": 186,
            "symbol": "¹⁸⁶Os",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 1.59,
            "isStable": true
      },
      {
            "massNumber": 187,
            "symbol": "¹⁸⁷Os",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 1.96,
            "isStable": true
      },
      {
            "massNumber": 188,
            "symbol": "¹⁸⁸Os",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 13.24,
            "isStable": true
      },
      {
            "massNumber": 189,
            "symbol": "¹⁸⁹Os",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 16.15,
            "isStable": true
      },
      {
            "massNumber": 190,
            "symbol": "¹⁹⁰Os",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 26.26,
            "isStable": true
      },
      {
            "massNumber": 192,
            "symbol": "¹⁹²Os",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 40.78,
            "isStable": true
      }
]
  },
  {
    id: 77,
    atomicNumber: 77,
    symbol: 'Ir',
    name: 'Iridium',
    category: 'transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d⁷ 6s²',
    atomicWeight: 192.21700,
    state: 'solid',
    density: 22.56000,
    meltingPoint: 2719.00,
    boilingPoint: 4701.00,
    electronegativity: 2.20,
    discoveredBy: 'Smithson Tennant',
    yearDiscovered: 1803,
    description: 'L\'iridium est très résistant à la corrosion.',
    uses: 'Bougies d\'allumage, creusets, pointes de stylos, alliages.',
    color: '#f07178',
    period: 6,
    gridRow: 6,
    gridColumn: 9,
    isotopes: [
      {
            "massNumber": 191,
            "symbol": "¹⁹¹Ir",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 37.3,
            "isStable": true
      },
      {
            "massNumber": 192,
            "symbol": "¹⁹²Ir",
            "halfLife": "73.83 jours",
            "halfLifeSeconds": 6379000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      },
      {
            "massNumber": 193,
            "symbol": "¹⁹³Ir",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 62.7,
            "isStable": true
      }
]
  },
  {
    id: 78,
    atomicNumber: 78,
    symbol: 'Pt',
    name: 'Platine',
    category: 'transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d⁹ 6s¹',
    atomicWeight: 195.08400,
    state: 'solid',
    density: 21.46000,
    meltingPoint: 2041.40,
    boilingPoint: 4098.00,
    electronegativity: 2.28,
    discoveredBy: 'Antonio de Ulloa',
    yearDiscovered: 1735,
    description: 'Le platine est un métal précieux utilisé en catalyse.',
    uses: 'Catalyseurs, joaillerie, électronique, laboratoires, anticancéreux.',
    color: '#f07178',
    period: 6,
    gridRow: 6,
    gridColumn: 10,
    isotopes: [
      {
            "massNumber": 190,
            "symbol": "¹⁹⁰Pt",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.01,
            "isStable": true
      },
      {
            "massNumber": 192,
            "symbol": "¹⁹²Pt",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.79,
            "isStable": true
      },
      {
            "massNumber": 194,
            "symbol": "¹⁹⁴Pt",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 32.97,
            "isStable": true
      },
      {
            "massNumber": 195,
            "symbol": "¹⁹⁵Pt",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 33.83,
            "isStable": true
      },
      {
            "massNumber": 196,
            "symbol": "¹⁹⁶Pt",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 25.24,
            "isStable": true
      },
      {
            "massNumber": 198,
            "symbol": "¹⁹⁸Pt",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 7.16,
            "isStable": true
      }
]
  },
  {
    id: 79,
    atomicNumber: 79,
    symbol: 'Au',
    name: 'Or',
    category: 'transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d¹⁰ 6s¹',
    atomicWeight: 196.96657,
    state: 'solid',
    density: 19.30000,
    meltingPoint: 1337.33,
    boilingPoint: 3129.00,
    electronegativity: 2.54,
    discoveredBy: 'Anciens',
    description: "L'or est un métal précieux, dense, mou, malléable et ductile, de couleur jaune brillante.",
    uses: 'Bijoux, monnaie, électronique, dentisterie, vitres anti-radiation.',
    color: '#f07178',
    period: 6,
    gridRow: 6,
    gridColumn: 11,
    isotopes: [
      {
            "massNumber": 197,
            "symbol": "¹⁹⁷Au",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 100,
            "isStable": true
      },
      {
            "massNumber": 198,
            "symbol": "¹⁹⁸Au",
            "halfLife": "2.69 jours",
            "halfLifeSeconds": 232416,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 80,
    atomicNumber: 80,
    symbol: 'Hg',
    name: 'Mercure',
    category: 'transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d¹⁰ 6s²',
    atomicWeight: 200.59000,
    state: 'liquid',
    density: 13.53360,
    meltingPoint: 234.32,
    boilingPoint: 629.88,
    electronegativity: 2.00,
    discoveredBy: 'Anciens',
    description: 'Le mercure est le seul métal liquide à température ambiante.',
    uses: 'Thermomètres, baromètres, lampes fluorescentes, amalgames dentaires.',
    color: '#f07178',
    period: 6,
    gridRow: 6,
    gridColumn: 12,
    isotopes: [
      {
            "massNumber": 196,
            "symbol": "¹⁹⁶Hg",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 0.15,
            "isStable": true
      },
      {
            "massNumber": 198,
            "symbol": "¹⁹⁸Hg",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 9.97,
            "isStable": true
      },
      {
            "massNumber": 199,
            "symbol": "¹⁹⁹Hg",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 16.87,
            "isStable": true
      },
      {
            "massNumber": 200,
            "symbol": "²⁰⁰Hg",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 23.1,
            "isStable": true
      },
      {
            "massNumber": 201,
            "symbol": "²⁰¹Hg",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 13.18,
            "isStable": true
      },
      {
            "massNumber": 202,
            "symbol": "²⁰²Hg",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 29.86,
            "isStable": true
      },
      {
            "massNumber": 204,
            "symbol": "²⁰⁴Hg",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 6.87,
            "isStable": true
      }
]
  },
  {
    id: 81,
    atomicNumber: 81,
    symbol: 'Tl',
    name: 'Thallium',
    category: 'post_transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d¹⁰ 6s² 6p¹',
    atomicWeight: 204.38330,
    state: 'solid',
    density: 11.85000,
    meltingPoint: 577.00,
    boilingPoint: 1746.00,
    electronegativity: 1.62,
    discoveredBy: 'William Crookes',
    yearDiscovered: 1861,
    description: 'Le thallium est toxique et utilisé en électronique.',
    uses: 'Détecteurs infrarouges, verre optique, semi-conducteurs.',
    color: '#89ddff',
    period: 6,
    gridRow: 6,
    gridColumn: 13,
    isotopes: [
      {
            "massNumber": 203,
            "symbol": "²⁰³Tl",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 29.52,
            "isStable": true
      },
      {
            "massNumber": 205,
            "symbol": "²⁰⁵Tl",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 70.48,
            "isStable": true
      }
]
  },
  {
    id: 82,
    atomicNumber: 82,
    symbol: 'Pb',
    name: 'Plomb',
    category: 'post_transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d¹⁰ 6s² 6p²',
    atomicWeight: 207.20000,
    state: 'solid',
    density: 11.34000,
    meltingPoint: 600.61,
    boilingPoint: 2022.00,
    electronegativity: 2.33,
    discoveredBy: 'Anciens',
    description: 'Le plomb est un métal lourd utilisé dans les batteries.',
    uses: 'Batteries, munitions, protection contre les radiations, soudure.',
    color: '#89ddff',
    period: 6,
    gridRow: 6,
    gridColumn: 14,
    isotopes: [
      {
            "massNumber": 204,
            "symbol": "²⁰⁴Pb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 1.4,
            "isStable": true
      },
      {
            "massNumber": 206,
            "symbol": "²⁰⁶Pb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 24.1,
            "isStable": true
      },
      {
            "massNumber": 207,
            "symbol": "²⁰⁷Pb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 22.1,
            "isStable": true
      },
      {
            "massNumber": 208,
            "symbol": "²⁰⁸Pb",
            "halfLife": "stable",
            "decayMode": [
                  "stable"
            ],
            "naturalAbundance": 52.4,
            "isStable": true
      },
      {
            "massNumber": 210,
            "symbol": "²¹⁰Pb",
            "halfLife": "22.3 ans",
            "halfLifeSeconds": 704000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 83,
    atomicNumber: 83,
    symbol: 'Bi',
    name: 'Bismuth',
    category: 'post_transition_metal',
    electronConfiguration: '[Xe] 4f¹⁴ 5d¹⁰ 6s² 6p³',
    atomicWeight: 208.98040,
    state: 'solid',
    density: 9.78700,
    meltingPoint: 544.40,
    boilingPoint: 1837.00,
    electronegativity: 2.02,
    discoveredBy: 'Claude François Geoffroy',
    yearDiscovered: 1753,
    description: 'Le bismuth a des cristaux colorés et est peu toxique.',
    uses: 'Alliages bas point de fusion, cosmétiques, médicaments.',
    color: '#89ddff',
    period: 6,
    gridRow: 6,
    gridColumn: 15,
    isotopes: [
      {
            "massNumber": 209,
            "symbol": "²⁰⁹Bi",
            "halfLife": "1.9e19 ans",
            "halfLifeSeconds": 6e+26,
            "decayMode": [
                  "alpha"
            ],
            "naturalAbundance": 100,
            "isStable": false
      }
]
  },
  {
    id: 84,
    atomicNumber: 84,
    symbol: 'Po',
    name: 'Polonium',
    category: 'metalloid',
    electronConfiguration: '[Xe] 4f¹⁴ 5d¹⁰ 6s² 6p⁴',
    atomicWeight: 209.00000,
    state: 'solid',
    density: 9.32000,
    meltingPoint: 527.00,
    boilingPoint: 1235.00,
    electronegativity: 2.00,
    discoveredBy: 'Marie Curie',
    yearDiscovered: 1898,
    description: 'Le polonium est hautement radioactif et dangereux.',
    uses: 'Sources de chaleur pour satellites, élimination de l\'électricité statique.',
    color: '#c3e88d',
    period: 6,
    gridRow: 6,
    gridColumn: 16,
    isotopes: [
      {
            "massNumber": 208,
            "symbol": "²⁰⁸Po",
            "halfLife": "2.90 ans",
            "halfLifeSeconds": 91500000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 209,
            "symbol": "²⁰⁹Po",
            "halfLife": "102 ans",
            "halfLifeSeconds": 3220000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 210,
            "symbol": "²¹⁰Po",
            "halfLife": "138.4 jours",
            "halfLifeSeconds": 11960000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 85,
    atomicNumber: 85,
    symbol: 'At',
    name: 'Astate',
    category: 'halogen',
    electronConfiguration: '[Xe] 4f¹⁴ 5d¹⁰ 6s² 6p⁵',
    atomicWeight: 210.00000,
    state: 'solid',
    density: 7.00000,
    meltingPoint: 575.00,
    boilingPoint: 610.00,
    electronegativity: 2.20,
    discoveredBy: 'Dale R. Corson',
    yearDiscovered: 1940,
    description: 'L\'astate est l\'halogène le plus lourd et très radioactif.',
    uses: 'Recherche en radiothérapie.',
    color: '#ffcb6b',
    period: 6,
    gridRow: 6,
    gridColumn: 17,
    isotopes: [
      {
            "massNumber": 210,
            "symbol": "²¹⁰At",
            "halfLife": "8.1 heures",
            "halfLifeSeconds": 29160,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 211,
            "symbol": "²¹¹At",
            "halfLife": "7.2 heures",
            "halfLifeSeconds": 25920,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 86,
    atomicNumber: 86,
    symbol: 'Rn',
    name: 'Radon',
    category: 'noble_gas',
    electronConfiguration: '[Xe] 4f¹⁴ 5d¹⁰ 6s² 6p⁶',
    atomicWeight: 222.00000,
    state: 'gas',
    density: 9.73000,
    meltingPoint: 202.00,
    boilingPoint: 211.50,
    discoveredBy: 'Friedrich Ernst Dorn',
    yearDiscovered: 1900,
    description: 'Le radon est un gaz noble radioactif et cancérigène.',
    uses: 'Radiothérapie, recherche en géophysique.',
    color: '#c792ea',
    period: 6,
    gridRow: 6,
    gridColumn: 18,
    isotopes: [
      {
            "massNumber": 220,
            "symbol": "²²⁰Rn",
            "halfLife": "55.6 secondes",
            "halfLifeSeconds": 55.6,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 222,
            "symbol": "²²²Rn",
            "halfLife": "3.82 jours",
            "halfLifeSeconds": 330048,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 87,
    atomicNumber: 87,
    symbol: 'Fr',
    name: 'Francium',
    category: 'alkali_metal',
    electronConfiguration: '[Rn] 7s¹',
    atomicWeight: 223.00000,
    state: 'solid',
    density: 1.87000,
    meltingPoint: 300.00,
    boilingPoint: 950.00,
    electronegativity: 0.70,
    discoveredBy: 'Marguerite Perey',
    yearDiscovered: 1939,
    description: 'Le francium est le métal alcalin le plus lourd et très rare.',
    uses: 'Recherche scientifique uniquement.',
    color: '#ff5370',
    period: 7,
    gridRow: 7,
    gridColumn: 1,
    isotopes: [
      {
            "massNumber": 223,
            "symbol": "²²³Fr",
            "halfLife": "22 min",
            "halfLifeSeconds": 1320,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 88,
    atomicNumber: 88,
    symbol: 'Ra',
    name: 'Radium',
    category: 'alkaline_earth_metal',
    electronConfiguration: '[Rn] 7s²',
    atomicWeight: 226.00000,
    state: 'solid',
    density: 5.50000,
    meltingPoint: 973.00,
    boilingPoint: 2010.00,
    electronegativity: 0.90,
    discoveredBy: 'Marie Curie',
    yearDiscovered: 1898,
    description: 'Le radium est hautement radioactif et luminescent.',
    uses: 'Anciennes peintures luminescentes, radiothérapie.',
    color: '#f78c6c',
    period: 7,
    gridRow: 7,
    gridColumn: 2,
    isotopes: [
      {
            "massNumber": 223,
            "symbol": "²²³Ra",
            "halfLife": "11.43 jours",
            "halfLifeSeconds": 987552,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 224,
            "symbol": "²²⁴Ra",
            "halfLife": "3.66 jours",
            "halfLifeSeconds": 316224,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 226,
            "symbol": "²²⁶Ra",
            "halfLife": "1600 ans",
            "halfLifeSeconds": 50500000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 228,
            "symbol": "²²⁸Ra",
            "halfLife": "5.75 ans",
            "halfLifeSeconds": 181000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 89,
    atomicNumber: 89,
    symbol: 'Ac',
    name: 'Actinium',
    category: 'actinide',
    electronConfiguration: '[Rn] 6d¹ 7s²',
    atomicWeight: 227.00000,
    state: 'solid',
    density: 10.07000,
    meltingPoint: 1323.00,
    boilingPoint: 3471.00,
    electronegativity: 1.10,
    discoveredBy: 'André-Louis Debierne',
    yearDiscovered: 1899,
    description: 'L\'actinium est le premier des actinides et radioactif.',
    uses: 'Source de neutrons, recherche.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 3,
    isotopes: [
      {
            "massNumber": 227,
            "symbol": "²²⁷Ac",
            "halfLife": "21.77 ans",
            "halfLifeSeconds": 687000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 90,
    atomicNumber: 90,
    symbol: 'Th',
    name: 'Thorium',
    category: 'actinide',
    electronConfiguration: '[Rn] 6d² 7s²',
    atomicWeight: 232.03806,
    state: 'solid',
    density: 11.72000,
    meltingPoint: 2115.00,
    boilingPoint: 5061.00,
    electronegativity: 1.30,
    discoveredBy: 'Jöns Jacob Berzelius',
    yearDiscovered: 1829,
    description: 'Le thorium est une source potentielle d\'énergie nucléaire.',
    uses: 'Réacteurs nucléaires, lampes à gaz, alliages.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 4,
    isotopes: [
      {
            "massNumber": 228,
            "symbol": "²²⁸Th",
            "halfLife": "1.91 ans",
            "halfLifeSeconds": 60300000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 230,
            "symbol": "²³⁰Th",
            "halfLife": "75380 ans",
            "halfLifeSeconds": 2380000000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 232,
            "symbol": "²³²Th",
            "halfLife": "14.05 milliards d'années",
            "halfLifeSeconds": 443000000000000000,
            "decayMode": [
                  "alpha"
            ],
            "naturalAbundance": 100,
            "isStable": false
      }
]
  },
  {
    id: 91,
    atomicNumber: 91,
    symbol: 'Pa',
    name: 'Protactinium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f² 6d¹ 7s²',
    atomicWeight: 231.03588,
    state: 'solid',
    density: 15.37000,
    meltingPoint: 1841.00,
    boilingPoint: 4300.00,
    discoveredBy: 'Kasimir Fajans',
    yearDiscovered: 1913,
    description: 'Le protactinium est un précurseur de l\'uranium.',
    uses: 'Recherche scientifique.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 5,
    isotopes: [
      {
            "massNumber": 231,
            "symbol": "²³¹Pa",
            "halfLife": "32760 ans",
            "halfLifeSeconds": 1030000000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 92,
    atomicNumber: 92,
    symbol: 'U',
    name: 'Uranium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f³ 6d¹ 7s²',
    atomicWeight: 238.02891,
    state: 'solid',
    density: 19.05000,
    meltingPoint: 1405.30,
    boilingPoint: 4404.00,
    electronegativity: 1.38,
    discoveredBy: 'Martin Heinrich Klaproth',
    yearDiscovered: 1789,
    description: 'L\'uranium est utilisé comme combustible nucléaire.',
    uses: 'Combustible nucléaire, armes nucléaires, blindage.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 6,
    isotopes: [
      {
            "massNumber": 233,
            "symbol": "²³³U",
            "halfLife": "159200 ans",
            "halfLifeSeconds": 5020000000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 234,
            "symbol": "²³⁴U",
            "halfLife": "245500 ans",
            "halfLifeSeconds": 7750000000000,
            "decayMode": [
                  "alpha"
            ],
            "naturalAbundance": 0.005,
            "isStable": false
      },
      {
            "massNumber": 235,
            "symbol": "²³⁵U",
            "halfLife": "703.8 millions d'années",
            "halfLifeSeconds": 22200000000000000,
            "decayMode": [
                  "alpha"
            ],
            "naturalAbundance": 0.72,
            "isStable": false
      },
      {
            "massNumber": 238,
            "symbol": "²³⁸U",
            "halfLife": "4.468 milliards d'années",
            "halfLifeSeconds": 141000000000000000,
            "decayMode": [
                  "alpha"
            ],
            "naturalAbundance": 99.275,
            "isStable": false
      }
]
  },
  {
    id: 93,
    atomicNumber: 93,
    symbol: 'Np',
    name: 'Neptunium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f⁴ 6d¹ 7s²',
    atomicWeight: 237.00000,
    state: 'solid',
    density: 20.45000,
    meltingPoint: 912.00,
    boilingPoint: 4447.00,
    discoveredBy: 'Edwin McMillan',
    yearDiscovered: 1940,
    description: 'Le neptunium est le premier élément transuranien synthétisé.',
    uses: 'Détecteurs de neutrons, recherche.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 7,
    isotopes: [
      {
            "massNumber": 237,
            "symbol": "²³⁷Np",
            "halfLife": "2.14 millions d'années",
            "halfLifeSeconds": 67500000000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 239,
            "symbol": "²³⁹Np",
            "halfLife": "2.36 jours",
            "halfLifeSeconds": 203904,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 94,
    atomicNumber: 94,
    symbol: 'Pu',
    name: 'Plutonium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f⁶ 7s²',
    atomicWeight: 244.00000,
    state: 'solid',
    density: 19.84000,
    meltingPoint: 912.50,
    boilingPoint: 3505.00,
    electronegativity: 1.28,
    discoveredBy: 'Glenn T. Seaborg',
    yearDiscovered: 1940,
    description: 'Le plutonium est utilisé dans les armes et réacteurs nucléaires.',
    uses: 'Armes nucléaires, combustible nucléaire, générateurs pour sondes spatiales.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 8,
    isotopes: [
      {
            "massNumber": 238,
            "symbol": "²³⁸Pu",
            "halfLife": "87.7 ans",
            "halfLifeSeconds": 2770000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 239,
            "symbol": "²³⁹Pu",
            "halfLife": "24110 ans",
            "halfLifeSeconds": 761000000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 240,
            "symbol": "²⁴⁰Pu",
            "halfLife": "6561 ans",
            "halfLifeSeconds": 207000000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 241,
            "symbol": "²⁴¹Pu",
            "halfLife": "14.35 ans",
            "halfLifeSeconds": 453000000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 95,
    atomicNumber: 95,
    symbol: 'Am',
    name: 'Américium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f⁷ 7s²',
    atomicWeight: 243.00000,
    state: 'solid',
    density: 12.00000,
    meltingPoint: 1449.00,
    boilingPoint: 2880.00,
    electronegativity: 1.30,
    discoveredBy: 'Glenn T. Seaborg',
    yearDiscovered: 1944,
    description: 'L\'américium est utilisé dans les détecteurs de fumée.',
    uses: 'Détecteurs de fumée, jauges industrielles, radiographie.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 9,
    isotopes: [
      {
            "massNumber": 241,
            "symbol": "²⁴¹Am",
            "halfLife": "432.2 ans",
            "halfLifeSeconds": 13600000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 243,
            "symbol": "²⁴³Am",
            "halfLife": "7370 ans",
            "halfLifeSeconds": 233000000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 96,
    atomicNumber: 96,
    symbol: 'Cm',
    name: 'Curium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f⁷ 6d¹ 7s²',
    atomicWeight: 247.00000,
    state: 'solid',
    density: 13.51000,
    meltingPoint: 1613.00,
    boilingPoint: 3383.00,
    electronegativity: 1.30,
    discoveredBy: 'Glenn T. Seaborg',
    yearDiscovered: 1944,
    description: 'Le curium est nommé d\'après Marie et Pierre Curie.',
    uses: 'Sources d\'énergie pour satellites, analyseurs de rayons X.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 10,
    isotopes: [
      {
            "massNumber": 242,
            "symbol": "²⁴²Cm",
            "halfLife": "162.8 jours",
            "halfLifeSeconds": 14070000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 244,
            "symbol": "²⁴⁴Cm",
            "halfLife": "18.1 ans",
            "halfLifeSeconds": 571000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 247,
            "symbol": "²⁴⁷Cm",
            "halfLife": "15.6 millions d'années",
            "halfLifeSeconds": 492000000000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 97,
    atomicNumber: 97,
    symbol: 'Bk',
    name: 'Berkélium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f⁹ 7s²',
    atomicWeight: 247.00000,
    state: 'solid',
    density: 14.79000,
    meltingPoint: 1259.00,
    boilingPoint: 2900.00,
    discoveredBy: 'Glenn T. Seaborg',
    yearDiscovered: 1949,
    description: 'Le berkélium est nommé d\'après Berkeley, Californie.',
    uses: 'Recherche scientifique uniquement.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 11,
    isotopes: [
      {
            "massNumber": 247,
            "symbol": "²⁴⁷Bk",
            "halfLife": "1380 ans",
            "halfLifeSeconds": 43600000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 249,
            "symbol": "²⁴⁹Bk",
            "halfLife": "330 jours",
            "halfLifeSeconds": 28510000,
            "decayMode": [
                  "beta-"
            ],
            "isStable": false
      }
]
  },
  {
    id: 98,
    atomicNumber: 98,
    symbol: 'Cf',
    name: 'Californium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f¹⁰ 7s²',
    atomicWeight: 251.00000,
    state: 'solid',
    density: 15.10000,
    meltingPoint: 1173.00,
    boilingPoint: 1743.00,
    discoveredBy: 'Glenn T. Seaborg',
    yearDiscovered: 1950,
    description: 'Le californium émet de puissantes sources de neutrons.',
    uses: 'Traitement du cancer, détection de métaux, prospection pétrolière.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 12,
    isotopes: [
      {
            "massNumber": 249,
            "symbol": "²⁴⁹Cf",
            "halfLife": "351 ans",
            "halfLifeSeconds": 11100000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 250,
            "symbol": "²⁵⁰Cf",
            "halfLife": "13.08 ans",
            "halfLifeSeconds": 413000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 251,
            "symbol": "²⁵¹Cf",
            "halfLife": "898 ans",
            "halfLifeSeconds": 28300000000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 99,
    atomicNumber: 99,
    symbol: 'Es',
    name: 'Einsteinium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f¹¹ 7s²',
    atomicWeight: 252.00000,
    state: 'solid',
    density: 8.84000,
    meltingPoint: 1133.00,
    discoveredBy: 'Albert Ghiorso',
    yearDiscovered: 1952,
    description: 'L\'einsteinium a été découvert dans les débris d\'essais nucléaires.',
    uses: 'Recherche scientifique uniquement.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 13,
    isotopes: [
      {
            "massNumber": 252,
            "symbol": "²⁵²Es",
            "halfLife": "471.7 jours",
            "halfLifeSeconds": 40760000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      },
      {
            "massNumber": 253,
            "symbol": "²⁵³Es",
            "halfLife": "20.47 jours",
            "halfLifeSeconds": 1769000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 100,
    atomicNumber: 100,
    symbol: 'Fm',
    name: 'Fermium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f¹² 7s²',
    atomicWeight: 257.00000,
    state: 'solid',
    discoveredBy: 'Albert Ghiorso',
    yearDiscovered: 1952,
    description: 'Le fermium est nommé d\'après Enrico Fermi.',
    uses: 'Recherche scientifique uniquement.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 14,
    isotopes: [
      {
            "massNumber": 257,
            "symbol": "²⁵⁷Fm",
            "halfLife": "100.5 jours",
            "halfLifeSeconds": 8683000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 101,
    atomicNumber: 101,
    symbol: 'Md',
    name: 'Mendélévium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f¹³ 7s²',
    atomicWeight: 258.00000,
    state: 'solid',
    discoveredBy: 'Albert Ghiorso',
    yearDiscovered: 1955,
    description: 'Le mendélévium est nommé d\'après Dmitri Mendeleïev.',
    uses: 'Recherche scientifique uniquement.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 15,
    isotopes: [
      {
            "massNumber": 258,
            "symbol": "²⁵⁸Md",
            "halfLife": "51.5 jours",
            "halfLifeSeconds": 4450000,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 102,
    atomicNumber: 102,
    symbol: 'No',
    name: 'Nobélium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f¹⁴ 7s²',
    atomicWeight: 259.00000,
    state: 'solid',
    discoveredBy: 'Albert Ghiorso',
    yearDiscovered: 1958,
    description: 'Le nobélium est nommé d\'après Alfred Nobel.',
    uses: 'Recherche scientifique uniquement.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 16,
    isotopes: [
      {
            "massNumber": 259,
            "symbol": "²⁵⁹No",
            "halfLife": "58 min",
            "halfLifeSeconds": 3480,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 103,
    atomicNumber: 103,
    symbol: 'Lr',
    name: 'Lawrencium',
    category: 'actinide',
    electronConfiguration: '[Rn] 5f¹⁴ 7s² 7p¹',
    atomicWeight: 266.00000,
    state: 'solid',
    discoveredBy: 'Albert Ghiorso',
    yearDiscovered: 1961,
    description: 'Le lawrencium est le dernier des actinides.',
    uses: 'Recherche scientifique uniquement.',
    color: '#ff5370',
    period: 7,
    gridRow: 10,
    gridColumn: 17,
    isotopes: [
      {
            "massNumber": 262,
            "symbol": "²⁶²Lr",
            "halfLife": "3.6 heures",
            "halfLifeSeconds": 12960,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 104,
    atomicNumber: 104,
    symbol: 'Rf',
    name: 'Rutherfordium',
    category: 'transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d² 7s²',
    atomicWeight: 267.00000,
    state: 'solid',
    discoveredBy: 'Albert Ghiorso',
    yearDiscovered: 1969,
    description: 'Le rutherfordium est nommé d\'après Ernest Rutherford.',
    uses: 'Recherche scientifique uniquement.',
    color: '#f07178',
    period: 7,
    gridRow: 7,
    gridColumn: 4,
    isotopes: [
      {
            "massNumber": 267,
            "symbol": "²⁶⁷Rf",
            "halfLife": "1.3 heures",
            "halfLifeSeconds": 4680,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 105,
    atomicNumber: 105,
    symbol: 'Db',
    name: 'Dubnium',
    category: 'transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d³ 7s²',
    atomicWeight: 268.00000,
    state: 'solid',
    discoveredBy: 'Albert Ghiorso',
    yearDiscovered: 1970,
    description: 'Le dubnium est nommé d\'après Dubna, Russie.',
    uses: 'Recherche scientifique uniquement.',
    color: '#f07178',
    period: 7,
    gridRow: 7,
    gridColumn: 5,
    isotopes: [
      {
            "massNumber": 268,
            "symbol": "²⁶⁸Db",
            "halfLife": "28 heures",
            "halfLifeSeconds": 100800,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 106,
    atomicNumber: 106,
    symbol: 'Sg',
    name: 'Seaborgium',
    category: 'transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d⁴ 7s²',
    atomicWeight: 269.00000,
    state: 'solid',
    discoveredBy: 'Albert Ghiorso',
    yearDiscovered: 1974,
    description: 'Le seaborgium est nommé d\'après Glenn T. Seaborg.',
    uses: 'Recherche scientifique uniquement.',
    color: '#f07178',
    period: 7,
    gridRow: 7,
    gridColumn: 6,
    isotopes: [
      {
            "massNumber": 271,
            "symbol": "²⁷¹Sg",
            "halfLife": "2.4 min",
            "halfLifeSeconds": 144,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 107,
    atomicNumber: 107,
    symbol: 'Bh',
    name: 'Bohrium',
    category: 'transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d⁵ 7s²',
    atomicWeight: 270.00000,
    state: 'solid',
    discoveredBy: 'Peter Armbruster',
    yearDiscovered: 1981,
    description: 'Le bohrium est nommé d\'après Niels Bohr.',
    uses: 'Recherche scientifique uniquement.',
    color: '#f07178',
    period: 7,
    gridRow: 7,
    gridColumn: 7,
    isotopes: [
      {
            "massNumber": 272,
            "symbol": "²⁷²Bh",
            "halfLife": "9.8 secondes",
            "halfLifeSeconds": 9.8,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 108,
    atomicNumber: 108,
    symbol: 'Hs',
    name: 'Hassium',
    category: 'transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d⁶ 7s²',
    atomicWeight: 277.00000,
    state: 'solid',
    discoveredBy: 'Peter Armbruster',
    yearDiscovered: 1984,
    description: 'Le hassium est nommé d\'après Hesse, Allemagne.',
    uses: 'Recherche scientifique uniquement.',
    color: '#f07178',
    period: 7,
    gridRow: 7,
    gridColumn: 8,
    isotopes: [
      {
            "massNumber": 277,
            "symbol": "²⁷⁷Hs",
            "halfLife": "11 min",
            "halfLifeSeconds": 660,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 109,
    atomicNumber: 109,
    symbol: 'Mt',
    name: 'Meitnérium',
    category: 'transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d⁷ 7s²',
    atomicWeight: 278.00000,
    state: 'solid',
    discoveredBy: 'Peter Armbruster',
    yearDiscovered: 1982,
    description: 'Le meitnérium est nommé d\'après Lise Meitner.',
    uses: 'Recherche scientifique uniquement.',
    color: '#f07178',
    period: 7,
    gridRow: 7,
    gridColumn: 9,
    isotopes: [
      {
            "massNumber": 278,
            "symbol": "²⁷⁸Mt",
            "halfLife": "7.6 secondes",
            "halfLifeSeconds": 7.6,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 110,
    atomicNumber: 110,
    symbol: 'Ds',
    name: 'Darmstadtium',
    category: 'transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d⁸ 7s²',
    atomicWeight: 281.00000,
    state: 'solid',
    discoveredBy: 'Peter Armbruster',
    yearDiscovered: 1994,
    description: 'Le darmstadtium est nommé d\'après Darmstadt, Allemagne.',
    uses: 'Recherche scientifique uniquement.',
    color: '#f07178',
    period: 7,
    gridRow: 7,
    gridColumn: 10,
    isotopes: [
      {
            "massNumber": 281,
            "symbol": "²⁸¹Ds",
            "halfLife": "11 secondes",
            "halfLifeSeconds": 11,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 111,
    atomicNumber: 111,
    symbol: 'Rg',
    name: 'Roentgenium',
    category: 'transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d⁹ 7s²',
    atomicWeight: 282.00000,
    state: 'solid',
    discoveredBy: 'Peter Armbruster',
    yearDiscovered: 1994,
    description: 'Le roentgenium est nommé d\'après Wilhelm Röntgen.',
    uses: 'Recherche scientifique uniquement.',
    color: '#f07178',
    period: 7,
    gridRow: 7,
    gridColumn: 11,
    isotopes: [
      {
            "massNumber": 282,
            "symbol": "²⁸²Rg",
            "halfLife": "2.1 min",
            "halfLifeSeconds": 126,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 112,
    atomicNumber: 112,
    symbol: 'Cn',
    name: 'Copernicium',
    category: 'transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d¹⁰ 7s²',
    atomicWeight: 285.00000,
    state: 'solid',
    discoveredBy: 'Sigurd Hofmann',
    yearDiscovered: 1996,
    description: 'Le copernicium est nommé d\'après Nicolas Copernic.',
    uses: 'Recherche scientifique uniquement.',
    color: '#f07178',
    period: 7,
    gridRow: 7,
    gridColumn: 12,
    isotopes: [
      {
            "massNumber": 285,
            "symbol": "²⁸⁵Cn",
            "halfLife": "29 secondes",
            "halfLifeSeconds": 29,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 113,
    atomicNumber: 113,
    symbol: 'Nh',
    name: 'Nihonium',
    category: 'post_transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d¹⁰ 7s² 7p¹',
    atomicWeight: 286.00000,
    state: 'solid',
    discoveredBy: 'Institut RIKEN',
    yearDiscovered: 2003,
    description: 'Le nihonium est nommé d\'après le Japon (Nihon).',
    uses: 'Recherche scientifique uniquement.',
    color: '#89ddff',
    period: 7,
    gridRow: 7,
    gridColumn: 13,
    isotopes: [
      {
            "massNumber": 286,
            "symbol": "²⁸⁶Nh",
            "halfLife": "9.5 secondes",
            "halfLifeSeconds": 9.5,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 114,
    atomicNumber: 114,
    symbol: 'Fl',
    name: 'Flerovium',
    category: 'post_transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d¹⁰ 7s² 7p²',
    atomicWeight: 289.00000,
    state: 'solid',
    discoveredBy: 'Institut de Dubna',
    yearDiscovered: 1998,
    description: 'Le flerovium est nommé d\'après Georgiy Flyorov.',
    uses: 'Recherche scientifique uniquement.',
    color: '#89ddff',
    period: 7,
    gridRow: 7,
    gridColumn: 14,
    isotopes: [
      {
            "massNumber": 289,
            "symbol": "²⁸⁹Fl",
            "halfLife": "2.6 secondes",
            "halfLifeSeconds": 2.6,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 115,
    atomicNumber: 115,
    symbol: 'Mc',
    name: 'Moscovium',
    category: 'post_transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d¹⁰ 7s² 7p³',
    atomicWeight: 290.00000,
    state: 'solid',
    discoveredBy: 'Institut de Dubna',
    yearDiscovered: 2003,
    description: 'Le moscovium est nommé d\'après Moscou.',
    uses: 'Recherche scientifique uniquement.',
    color: '#89ddff',
    period: 7,
    gridRow: 7,
    gridColumn: 15,
    isotopes: [
      {
            "massNumber": 290,
            "symbol": "²⁹⁰Mc",
            "halfLife": "0.65 secondes",
            "halfLifeSeconds": 0.65,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 116,
    atomicNumber: 116,
    symbol: 'Lv',
    name: 'Livermorium',
    category: 'post_transition_metal',
    electronConfiguration: '[Rn] 5f¹⁴ 6d¹⁰ 7s² 7p⁴',
    atomicWeight: 293.00000,
    state: 'solid',
    discoveredBy: 'Institut de Dubna',
    yearDiscovered: 2000,
    description: 'Le livermorium est nommé d\'après Livermore, Californie.',
    uses: 'Recherche scientifique uniquement.',
    color: '#89ddff',
    period: 7,
    gridRow: 7,
    gridColumn: 16,
    isotopes: [
      {
            "massNumber": 293,
            "symbol": "²⁹³Lv",
            "halfLife": "0.061 secondes",
            "halfLifeSeconds": 0.061,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 117,
    atomicNumber: 117,
    symbol: 'Ts',
    name: 'Tennessine',
    category: 'halogen',
    electronConfiguration: '[Rn] 5f¹⁴ 6d¹⁰ 7s² 7p⁵',
    atomicWeight: 294.00000,
    state: 'solid',
    discoveredBy: 'Institut de Dubna',
    yearDiscovered: 2009,
    description: 'Le tennessine est nommé d\'après le Tennessee.',
    uses: 'Recherche scientifique uniquement.',
    color: '#ffcb6b',
    period: 7,
    gridRow: 7,
    gridColumn: 17,
    isotopes: [
      {
            "massNumber": 294,
            "symbol": "²⁹⁴Ts",
            "halfLife": "0.051 secondes",
            "halfLifeSeconds": 0.051,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
  {
    id: 118,
    atomicNumber: 118,
    symbol: 'Og',
    name: 'Oganesson',
    category: 'noble_gas',
    electronConfiguration: '[Rn] 5f¹⁴ 6d¹⁰ 7s² 7p⁶',
    atomicWeight: 294.00000,
    state: 'solid',
    discoveredBy: 'Institut de Dubna',
    yearDiscovered: 2002,
    description: 'L\'oganesson est nommé d\'après Yuri Oganessian et complète le tableau périodique.',
    uses: 'Recherche scientifique uniquement.',
    color: '#c792ea',
    period: 7,
    gridRow: 7,
    gridColumn: 18,
    isotopes: [
      {
            "massNumber": 294,
            "symbol": "²⁹⁴Og",
            "halfLife": "0.00069 secondes",
            "halfLifeSeconds": 0.00069,
            "decayMode": [
                  "alpha"
            ],
            "isStable": false
      }
]
  },
];

// Injection automatique des données d'ionisation dans chaque élément
elements.forEach(el => {
  const ions = elementIons[el.atomicNumber];
  if (ions && ions.length > 0) {
    el.ions = ions;
  }

  // Injection des utilisations des isotopes
  const elementIsotopeUses = isotopeUses[el.atomicNumber];
  if (elementIsotopeUses && el.isotopes) {
    el.isotopes.forEach(iso => {
      const use = elementIsotopeUses[iso.massNumber];
      if (use) iso.uses = use;
    });
  }
});

export function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    'alkali_metal': '#ff5370',
    'alkaline_earth_metal': '#f78c6c',
    'transition_metal': '#f07178',
    'post_transition_metal': '#89ddff',
    'metalloid': '#c3e88d',
    'nonmetal': '#64ffda',
    'halogen': '#ffcb6b',
    'noble_gas': '#c792ea',
    'lanthanide': '#c792ea',
    'actinide': '#ff5370',
  };
  return colors[category] || '#64ffda';
}

export function getStateColor(state: string): string {
  const colors: Record<string, string> = {
    'solid': '#82aaff',
    'liquid': '#89ddff',
    'gas': '#c792ea',
  };
  return colors[state] || '#82aaff';
}
